import axios from 'axios';
import { logout as userLogout } from '../store/actions/authActions';
import { logout as adminLogout } from '../store/actions/adminAuthActions';
import apiCache from '../utils/apiCache';
import { resolveBrevoEventFilter } from '../constants/emailMonitor';

// Configuration de base de l'API
const API_BASE_URLS = {
    dev: 'https://pca-api.sandbox-orakleweather.com/',
    preprod: 'https://pca-api.sandbox-orakleweather.com/',
    prod: 'https://pca-api.sandbox-orakleweather.com/'
};

// En développement, utiliser le proxy pour éviter les problèmes CORS
let currentApiUrl = API_BASE_URLS.dev;
let authToken = null;
let adminAuthToken = null;
let storeInstance = null;
let awsAccessKeyId = null;
let awsSecretAccessKey = null;

export const setApiStore = (store) => {
    storeInstance = store;
};

export const setApiUrl = (env) => {
    currentApiUrl = API_BASE_URLS[env] || API_BASE_URLS.dev;

};

export const setAuthToken = (token) => {
    authToken = token;
};

export const getAuthToken = () => {
    return authToken;
};

export const setAdminAuthToken = (token) => {
    adminAuthToken = token;
    if (token) {
        localStorage.setItem('admin_access_token', token);
    } else {
        localStorage.removeItem('admin_access_token');
    }
};

// Initialiser le token admin au chargement si présent
const savedAdminToken = localStorage.getItem('admin_access_token');
if (savedAdminToken) {
    adminAuthToken = savedAdminToken;
}

// Initialiser les clés AWS depuis les variables d'environnement au démarrage (priorité)
const envAwsAccessKeyId = process.env.REACT_APP_AWS_ACCESS_KEY_ID;
const envAwsSecretAccessKey = process.env.REACT_APP_AWS_SECRET_ACCESS_KEY;

if (envAwsAccessKeyId && envAwsSecretAccessKey) {
    awsAccessKeyId = envAwsAccessKeyId;
    awsSecretAccessKey = envAwsSecretAccessKey;
    // Stocker aussi dans localStorage pour persistance
    localStorage.setItem('aws_access_key_id', envAwsAccessKeyId);
    localStorage.setItem('aws_secret_access_key', envAwsSecretAccessKey);
} else {
    // Si pas de variables d'environnement, charger depuis localStorage
    const savedAwsAccessKeyId = localStorage.getItem('aws_access_key_id');
    const savedAwsSecretAccessKey = localStorage.getItem('aws_secret_access_key');
    if (savedAwsAccessKeyId && savedAwsSecretAccessKey) {
        awsAccessKeyId = savedAwsAccessKeyId;
        awsSecretAccessKey = savedAwsSecretAccessKey;
    }
}

export const getAdminAuthToken = () => {
    return adminAuthToken || localStorage.getItem('admin_access_token');
};

// Gestion des clés d'accès AWS
export const setAwsCredentials = (accessKeyId, secretAccessKey) => {
    awsAccessKeyId = accessKeyId;
    awsSecretAccessKey = secretAccessKey;

    if (accessKeyId && secretAccessKey) {
        localStorage.setItem('aws_access_key_id', accessKeyId);
        localStorage.setItem('aws_secret_access_key', secretAccessKey);
    } else {
        localStorage.removeItem('aws_access_key_id');
        localStorage.removeItem('aws_secret_access_key');
    }
};

export const getAwsAccessKeyId = () => {
    // Priorité: variable d'environnement > variable en mémoire > localStorage
    return process.env.REACT_APP_AWS_ACCESS_KEY_ID || awsAccessKeyId || localStorage.getItem('aws_access_key_id');
};

export const getAwsSecretAccessKey = () => {
    // Priorité: variable d'environnement > variable en mémoire > localStorage
    return process.env.REACT_APP_AWS_SECRET_ACCESS_KEY || awsSecretAccessKey || localStorage.getItem('aws_secret_access_key');
};

export const clearAwsCredentials = () => {
    awsAccessKeyId = null;
    awsSecretAccessKey = null;
    localStorage.removeItem('aws_access_key_id');
    localStorage.removeItem('aws_secret_access_key');
};

// Instance axios avec intercepteurs
const apiClient = axios.create({
    timeout: 30000,
});

apiClient.interceptors.request.use(
    (config) => {
        // Priorité au token admin si disponible, sinon token utilisateur
        const adminToken = getAdminAuthToken();
        const userToken = authToken;

        if (adminToken) {
            config.headers.Authorization = `Bearer ${adminToken}`;
        } else if (userToken) {
            config.headers.Authorization = `Bearer ${userToken}`;
        }

        // Laisser le navigateur générer le Content-Type multipart avec sa boundary.
        // Forcer application/json sur un FormData sérialiserait le fichier en objet vide.
        if (config.data instanceof FormData) {
            delete config.headers['Content-Type'];
        } else if (!config.headers['Content-Type']) {
            config.headers['Content-Type'] = 'application/json';
        }
        if (!config.headers['Accept']) {
            config.headers['Accept'] = 'application/json';
        }
        // Note: User-Agent ne peut pas être défini côté client pour des raisons de sécurité du navigateur

        // Définir l'URL de base
        // Toujours utiliser l'URL configurée (http://http://127.0.0.1:8000/)
        config.baseURL = currentApiUrl;

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

apiClient.interceptors.response.use(
    (response) => {
        // Mettre en cache les réponses GET réussies
        const config = response.config;
        if (config.shouldCache && config.cacheKey && response.status === 200) {
            // TTL personnalisé basé sur l'endpoint (optionnel)
            let ttl = null;

            // Cache plus long pour les données de référence (clients, partenaires, etc.)
            if (config.url?.includes('/clients/') ||
                config.url?.includes('/partners/') ||
                config.url?.includes('/activity-references/')) {
                ttl = 10 * 60 * 1000; // 10 minutes
            }
            // Cache moyen pour les listes paginées
            else if (config.url?.includes('/pagination/')) {
                ttl = 2 * 60 * 1000; // 2 minutes
            }
            // Cache court par défaut pour les détails
            else {
                ttl = 5 * 60 * 1000; // 5 minutes
            }

            apiCache.set(config.cacheKey, response.data, ttl);
        }

        // Ajouter un flag pour indiquer si la réponse vient du cache
        if (config.fromCache) {
            response.fromCache = true;
        }

        return response;
    },
    (error) => {
        // Gérer les erreurs 401 (non autorisé)
        if (error.response?.status === 401) {
            const isAdminRoute = typeof window !== 'undefined' && window.location.pathname.startsWith('/admin');

            if (storeInstance) {
                storeInstance.dispatch(adminLogout());
                storeInstance.dispatch(userLogout());
            }

            setAdminAuthToken(null);
            setAuthToken(null);
            localStorage.removeItem('admin_access_token');
            localStorage.removeItem('admin_refresh_token');
            localStorage.removeItem('admin_session_id');
            localStorage.removeItem('authToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('sessionId');

            if (typeof window !== 'undefined') {
                const targetPath = isAdminRoute ? '/admin/login' : '/login';
                if (window.location.pathname !== targetPath) {
                    window.location.replace(targetPath);
                }
            }
        }

        // Gérer les erreurs 403 avec "Token blocked"
        if (error.response?.status === 403) {
            const errorDetail = error.response?.data?.detail || '';
            const isTokenBlocked = errorDetail === 'Token blocked' || errorDetail.includes('Token blocked');

            if (isTokenBlocked) {
                const isAdminRoute = typeof window !== 'undefined' && window.location.pathname.startsWith('/admin');

                if (storeInstance) {
                    storeInstance.dispatch(adminLogout());
                    storeInstance.dispatch(userLogout());
                }

                setAdminAuthToken(null);
                setAuthToken(null);
                localStorage.removeItem('admin_access_token');
                localStorage.removeItem('admin_refresh_token');
                localStorage.removeItem('admin_session_id');
                localStorage.removeItem('authToken');
                localStorage.removeItem('refreshToken');
                localStorage.removeItem('sessionId');

                if (typeof window !== 'undefined') {
                    const targetPath = isAdminRoute ? '/admin/login' : '/login';
                    if (window.location.pathname !== targetPath) {
                        window.location.replace(targetPath);
                    }
                }
            }
        }

        return Promise.reject(error);
    }
);

// Sauvegarder la méthode GET originale avant de la remplacer
const originalGet = apiClient.get.bind(apiClient);

/**
 * Fonction wrapper pour les requêtes GET avec cache
 */
const cachedGet = async (url, config = {}) => {
    // Vérifier si le refresh est forcé
    if (config.forceRefresh) {
        delete config.forceRefresh;
        const response = await originalGet(url, config);
        return response;
    }

    // Générer la clé de cache
    const params = config.params || {};
    const cacheKey = apiCache.generateKey(url, params, 'GET');

    // Vérifier le cache
    const cachedData = apiCache.get(cacheKey);
    if (cachedData) {
        // Retourner une réponse mockée avec les données en cache
        return {
            data: cachedData,
            status: 200,
            statusText: 'OK',
            headers: {},
            config: { ...config, url, fromCache: true },
            fromCache: true,
        };
    }

    // Si pas dans le cache, faire l'appel API et mettre en cache
    const response = await originalGet(url, config);

    // Déterminer le TTL basé sur l'URL
    let ttl = null;
    if (url.includes('/clients/') ||
        url.includes('/partners/') ||
        url.includes('/activity-references/')) {
        ttl = 10 * 60 * 1000; // 10 minutes
    } else if (url.includes('/pagination/')) {
        ttl = 2 * 60 * 1000; // 2 minutes
    } else {
        ttl = 5 * 60 * 1000; // 5 minutes
    }

    // Mettre en cache la réponse si elle est réussie
    if (response.status === 200 && response.data) {
        apiCache.set(cacheKey, response.data, ttl);
    }

    return response;
};

/**
 * Fonction pour invalider le cache lors des opérations POST/PUT/DELETE
 */
const invalidateCacheForUrl = (url) => {
    // Invalider toutes les entrées de cache qui contiennent cette URL de base
    apiCache.invalidateByUrl(url);
};

// Wrapper pour apiClient qui utilise le cache pour les GET
apiClient.get = function (url, config = {}) {
    // Utiliser le cache uniquement pour les requêtes GET
    if (!config.skipCache) {
        return cachedGet(url, config);
    }
    delete config.skipCache;
    return originalGet(url, config);
};

// Wrapper pour invalider le cache lors des POST/PUT/DELETE
const originalPost = apiClient.post.bind(apiClient);
apiClient.post = async function (url, data, config = {}) {
    const response = await originalPost(url, data, config);

    // Invalider le cache pour les URLs concernées
    if (url.includes('/clients/') || url.includes('/clients')) {
        invalidateCacheForUrl('/clients');
    } else if (url.includes('/partners/') || url.includes('/partners')) {
        invalidateCacheForUrl('/partners');
    } else if (url.includes('/trips/') || url.includes('/trips')) {
        invalidateCacheForUrl('/trips');
    } else if (url.includes('/policies/') || url.includes('/policies')) {
        invalidateCacheForUrl('/policies');
    } else if (url.includes('/claims/') || url.includes('/claims')) {
        invalidateCacheForUrl('/claims');
    } else if (url.includes('/mailing-list')) {
        invalidateCacheForUrl('/mailing-list');
    } else if (url.includes('/subcoverage') || url.includes('/subcoverages')) {
        invalidateCacheForUrl('/subcoverages');
    }

    return response;
};

const originalPut = apiClient.put.bind(apiClient);
apiClient.put = async function (url, data, config = {}) {
    const response = await originalPut(url, data, config);

    // Invalider le cache pour les URLs concernées
    if (url.includes('/clients/') || url.includes('/clients')) {
        invalidateCacheForUrl('/clients');
    } else if (url.includes('/partners/') || url.includes('/partners')) {
        invalidateCacheForUrl('/partners');
    } else if (url.includes('/trips/') || url.includes('/trips')) {
        invalidateCacheForUrl('/trips');
    } else if (url.includes('/policies/') || url.includes('/policies')) {
        invalidateCacheForUrl('/policies');
    } else if (url.includes('/claims/') || url.includes('/claims')) {
        invalidateCacheForUrl('/claims');
    } else if (url.includes('/mailing-list')) {
        invalidateCacheForUrl('/mailing-list');
    } else if (url.includes('/subcoverage') || url.includes('/subcoverages')) {
        invalidateCacheForUrl('/subcoverages');
    }

    return response;
};

const originalDelete = apiClient.delete.bind(apiClient);
apiClient.delete = async function (url, config = {}) {
    const response = await originalDelete(url, config);

    // Invalider le cache pour les URLs concernées
    if (url.includes('/activities')) {
        invalidateCacheForUrl('/activities');
    }
    if (url.includes('/clients/') || url.includes('/clients')) {
        invalidateCacheForUrl('/clients');
    } else if (url.includes('/partners/') || url.includes('/partners')) {
        invalidateCacheForUrl('/partners');
    } else if (url.includes('/trips/') || url.includes('/trips')) {
        invalidateCacheForUrl('/trips');
    } else if (url.includes('/policies/') || url.includes('/policies')) {
        invalidateCacheForUrl('/policies');
    } else if (url.includes('/claims/') || url.includes('/claims')) {
        invalidateCacheForUrl('/claims');
    } else if (url.includes('/mailing-list')) {
        invalidateCacheForUrl('/mailing-list');
    } else if (url.includes('/subcoverage') || url.includes('/subcoverages')) {
        invalidateCacheForUrl('/subcoverages');
    }

    return response;
};

// Exporter les fonctions de gestion du cache
export const clearApiCache = () => {
    apiCache.clear();
};

export const invalidateApiCache = (pattern) => {
    apiCache.invalidate(pattern);
};

export const getApiCacheStats = () => {
    return apiCache.getStats();
};

// Fonctions API

// Authentification
export const login = async (email, password) => {
    const response = await apiClient.post('login', {
        email,
        password,
    });
    return response.data;
};

// MFA for normal users (same endpoints as admin but using user token)
export const verifyMfa = async (mfaCode, sessionId) => {
    const response = await apiClient.post('login/verify-mfa', {
        mfa_code: mfaCode,
        session_id: sessionId,
    });
    return response.data;
};

export const resetMfa = async (sessionId) => {
    const response = await apiClient.post('reset-mfa', {
        session_id: sessionId,
    });
    return response.data;
};

export const forgotPassword = async (email) => {
    const response = await apiClient.post('users/me/forgot_password', {
        user_email: email,
    });
    return response.data;
};

export const resetPassword = async (token, password, confirmPassword) => {
    const response = await apiClient.post(`users/me/reset_password/?access_token=${token}`, {
        password,
        confirm_password: confirmPassword,
    });
    return response.data;
};

// Admin Authentication APIs
export const adminLogin = async (email, password) => {
    const response = await apiClient.post('admin/login', {
        email,
        password,
    });
    return response.data;
};

export const adminVerifyMfa = async (mfaCode, sessionId) => {
    const response = await apiClient.post('admin/login/verify-mfa', {
        mfa_code: mfaCode,
        session_id: sessionId,
    });
    return response.data;
};

export const adminResetMfa = async (sessionId) => {
    const response = await apiClient.post('admin/reset-mfa', {
        session_id: sessionId,
    });
    return response.data;
};

export const resetMfaToken = async (email) => {
    const response = await apiClient.get(`admin/mfa/reset-token?email=${encodeURIComponent(email)}`);
    return response.data;
};

export const getSecureQR = async (token, email) => {
    const response = await apiClient.get(`admin/mfa/secure-qr-data?token=${encodeURIComponent(token)}&email=${encodeURIComponent(email)}`);
    return response.data;
};

export const adminResetPassword = async (email) => {
    const response = await apiClient.post('users/me/forgot_password', {
        user_email: email,
    });
    return response.data;
};

// Admin Security APIs
export const blockTokens = async (email) => {
    const response = await apiClient.post('admin/security/block-tokens', {
        email,
    });
    return response.data;
};

// Statistics API
export const getStatistics = async (params = {}) => {
    const {
        skip = 0,
        limit = 10,
    } = params;

    const queryParams = new URLSearchParams();
    queryParams.append('skip', skip.toString());
    queryParams.append('limit', limit.toString());

    const response = await apiClient.get(`api/statistics?${queryParams.toString()}`);
    return response.data;
};

// API Forecasts
export const getForecastTrips = async (params = {}) => {
    const {
        skip = 0,
        limit = 10,
        policy_code,
        activity_start_date,
        trip_day,
        reservation_number,
        client_first_name,
        client_last_name,
        client_email,
        observation_id,
    } = params;

    const queryParams = new URLSearchParams();
    queryParams.append('skip', skip.toString());
    queryParams.append('limit', limit.toString());

    if (policy_code) queryParams.append('policy_code', policy_code);
    if (activity_start_date) queryParams.append('activity_start_date', activity_start_date);
    if (trip_day) queryParams.append('trip_day', trip_day);
    if (reservation_number) queryParams.append('reservation_number', reservation_number);
    if (client_first_name) queryParams.append('client_first_name', client_first_name);
    if (client_last_name) queryParams.append('client_last_name', client_last_name);
    if (client_email) queryParams.append('client_email', client_email);
    if (observation_id) queryParams.append('observation_id', observation_id);

    const response = await apiClient.get(`api/pagination/trips/forecasts?${queryParams.toString()}`);
    return response.data;
};

export const getForecastActivities = async () => {
    const response = await apiClient.get('api/forecasts');
    return response.data;
};

export const getActivityForecasts = async (params = {}) => {
    const {
        skip = 0,
        limit = 10,
        policy_code,
        activity_start_date,
        reservation_number,
        client_first_name,
        client_last_name,
    } = params;

    const queryParams = new URLSearchParams();
    queryParams.append('skip', skip.toString());
    queryParams.append('limit', limit.toString());

    if (policy_code) queryParams.append('policy_code', policy_code);
    if (activity_start_date) queryParams.append('activity_start_date', activity_start_date);
    if (reservation_number) queryParams.append('reservation_number', reservation_number);
    if (client_first_name) queryParams.append('client_first_name', client_first_name);
    if (client_last_name) queryParams.append('client_last_name', client_last_name);

    const response = await apiClient.get(`api/pagination/forecasts?${queryParams.toString()}`);
    return response.data;
};

export const getActivityForecastById = async (forecastId) => {
    const response = await apiClient.get(`api/forecast/${forecastId}`);
    return response.data;
};

export const lookupPolicy = async ({ policy_code, reservation_number } = {}) => {
    const qs = new URLSearchParams();
    if (policy_code) qs.append('policy_code', policy_code);
    if (reservation_number) qs.append('reservation_number', reservation_number);
    const queryString = qs.toString();
    if (!queryString) {
        throw new Error('policy_code ou reservation_number requis');
    }
    const response = await apiClient.get(`api/policies/lookup?${queryString}`);
    return response.data;
};

export const checkManualActivityForecast = async (payload, queryParams = {}) => {
    const { policy_code, reservation_number } = queryParams;
    const qs = new URLSearchParams();
    if (policy_code) qs.append('policy_code', policy_code);
    if (reservation_number) qs.append('reservation_number', reservation_number);
    const queryString = qs.toString();
    const url = queryString
        ? `api/policies/forecast/manual/check?${queryString}`
        : 'api/policies/forecast/manual/check';
    const response = await apiClient.post(url, payload, {
        timeout: 300000,
    });
    return response.data;
};

export const checkManualGreenRiverForecast = async (payload, queryParams = {}) => {
    const { policy_code, reservation_number } = queryParams;
    const qs = new URLSearchParams();
    if (policy_code) qs.append('policy_code', policy_code);
    if (reservation_number) qs.append('reservation_number', reservation_number);
    const queryString = qs.toString();
    const url = queryString
        ? `api/green-river/policies/forecast/manual/check?${queryString}`
        : 'api/green-river/policies/forecast/manual/check';
    const response = await apiClient.post(url, payload, {
        timeout: 300000,
    });
    return response.data;
};

export const getTripForecastById = async (forecastId) => {
    const response = await apiClient.get(`api/trips/forecast/${forecastId}`);
    return response.data;
};

export const deleteActivityForecast = async (forecastId) => {
    const response = await apiClient.delete(`api/forecasts/${forecastId}`);
    return response.data;
};

export const deleteTripForecast = async (forecastId) => {
    const response = await apiClient.delete(`api/forecasts/${forecastId}`);
    return response.data;
};

export const getForecastsNotTriggeredTrips = async (params = {}) => {
    const {
        skip = 0,
        limit = 10,
        policy_code,
        reservation_number,
        client_email,
        created_date,
        company_name,
        activity_end_date_from,
        activity_end_date_to,
    } = params;

    const queryParams = new URLSearchParams();
    queryParams.append('skip', skip.toString());
    queryParams.append('limit', limit.toString());

    if (policy_code) queryParams.append('policy_code', policy_code);
    if (reservation_number) queryParams.append('reservation_number', reservation_number);
    if (client_email) queryParams.append('client_email', client_email);
    if (created_date) queryParams.append('created_date', created_date);
    if (company_name && String(company_name).trim() !== '') {
        queryParams.append('company_name', String(company_name).trim());
    }
    if (activity_end_date_from) queryParams.append('activity_end_date_from', activity_end_date_from);
    if (activity_end_date_to) queryParams.append('activity_end_date_to', activity_end_date_to);

    const response = await apiClient.get(`api/pagination/trips/forecasts-not-triggered?${queryParams.toString()}`);
    return response.data;
};

export const getActivityForecastsNotTriggered = async (params = {}) => {
    const {
        skip = 0,
        limit = 10,
        policy_code,
        reservation_number,
        client_email,
        created_date,
        company_name,
    } = params;

    const queryParams = new URLSearchParams();
    queryParams.append('skip', skip.toString());
    queryParams.append('limit', limit.toString());

    if (policy_code) queryParams.append('policy_code', policy_code);
    if (reservation_number) queryParams.append('reservation_number', reservation_number);
    if (client_email) queryParams.append('client_email', client_email);
    if (created_date) queryParams.append('created_date', created_date);
    if (company_name && String(company_name).trim() !== '') {
        queryParams.append('company_name', String(company_name).trim());
    }

    const response = await apiClient.get(`api/pagination/forecasts-not-triggered?${queryParams.toString()}`);
    return response.data;
};

export const getForecastNotTriggeredById = async (forecastNotTriggeredId) => {
    const response = await apiClient.get(`api/trips/forecasts-not-triggered/${forecastNotTriggeredId}`);
    return response.data;
};

export const deleteTripForecastNotTriggered = async (forecastNotTriggeredId) => {
    const response = await apiClient.delete(`api/trips/forecasts-not-triggered/${forecastNotTriggeredId}`);
    return response.data;
};

export const deleteTripCheckClaim = async (checkClaimId) => {
    const response = await apiClient.delete(`api/trips/check-claims/${checkClaimId}`);
    return response.data;
};

export const getActivityForecastNotTriggeredById = async (forecastNotTriggeredId) => {
    const response = await apiClient.get(`api/forecasts-not-triggered/${forecastNotTriggeredId}`);
    return response.data;
};

// API Forecast Triggered Email
export const sendForecastTriggeredBulkEmail = async (partnerNames = []) => {
    const queryParams = new URLSearchParams();
    if (Array.isArray(partnerNames) && partnerNames.length > 0) {
        partnerNames
            .filter((name) => typeof name === 'string' && name.trim() !== '')
            .forEach((name) => queryParams.append('partner_names', name.trim()));
    }
    const queryString = queryParams.toString();
    const url = queryString
        ? `api/ecmwf/trips/forecast-triggered/bulk-send-email?${queryString}`
        : 'api/ecmwf/trips/forecast-triggered/bulk-send-email';
    const response = await apiClient.get(url, { timeout: 0 });
    return response.data;
};

// API Forecast Triggered SMS
export const sendForecastTriggeredBulkSms = async (partnerNames = []) => {
    const queryParams = new URLSearchParams();
    if (Array.isArray(partnerNames) && partnerNames.length > 0) {
        partnerNames
            .filter((name) => typeof name === 'string' && name.trim() !== '')
            .forEach((name) => queryParams.append('partner_names', name.trim()));
    }
    const queryString = queryParams.toString();
    const url = queryString
        ? `api/ecmwf/trips/forecast-triggered/bulk-send-sms?${queryString}`
        : 'api/ecmwf/trips/forecast-triggered/bulk-send-sms';
    const response = await apiClient.get(url, { timeout: 0 });
    return response.data;
};

export const backupTripsScheduledForecast = async (params = {}) => {
    const { run_date, policy_code } = params;
    const queryParams = new URLSearchParams();
    if (run_date && String(run_date).trim() !== '') {
        queryParams.append('run_date', String(run_date).trim());
    }
    if (policy_code && String(policy_code).trim() !== '') {
        queryParams.append('policy_code', String(policy_code).trim());
    }
    const queryString = queryParams.toString();
    const url = queryString
        ? `api/backup/ecmwf-v2/trips/scheduled-forecast?${queryString}`
        : 'api/backup/ecmwf-v2/trips/scheduled-forecast';
    const response = await apiClient.get(url, { timeout: 0, skipCache: true });
    return response.data;
};

/**
 * Standard — check forecast backup par data_source (ECMWF ou METEOMATICS).
 * GET /api/standard/trips/backup/check-forecasts/{data_source}
 * Query optionnelles : run_date, policy_code.
 */
export const backupStandardCheckForecasts = async (params = {}) => {
    const dataSource = String(params.data_source || 'ECMWF').trim() || 'ECMWF';
    const queryParams = new URLSearchParams();
    if (params.run_date && String(params.run_date).trim() !== '') {
        queryParams.append('run_date', String(params.run_date).trim());
    }
    if (params.policy_code && String(params.policy_code).trim() !== '') {
        queryParams.append('policy_code', String(params.policy_code).trim());
    }
    const queryString = queryParams.toString();
    const path = `api/standard/trips/backup/check-forecasts/${encodeURIComponent(dataSource)}`;
    const url = queryString ? `${path}?${queryString}` : path;
    const response = await apiClient.get(url, { timeout: 0, skipCache: true });
    return response.data;
};

/**
 * Standard — backup check observation par data_source et trip_day.
 * GET /api/standard/trips/backup/check-observations
 * Query obligatoires : data_source (METEOFRANCE | METEOSTAT), trip_day (YYYY-MM-DD).
 * Query optionnelle : policy_code.
 */
export const backupStandardCheckObservations = async (params = {}) => {
    const dataSource = String(params.data_source || 'METEOFRANCE').trim() || 'METEOFRANCE';
    const tripDay = String(params.trip_day || '').trim();
    const queryParams = new URLSearchParams();
    queryParams.append('data_source', dataSource);
    if (tripDay) {
        queryParams.append('trip_day', tripDay);
    }
    if (params.policy_code && String(params.policy_code).trim() !== '') {
        queryParams.append('policy_code', String(params.policy_code).trim());
    }
    const url = `api/standard/trips/backup/check-observations?${queryParams.toString()}`;
    const response = await apiClient.get(url, { timeout: 0, skipCache: true });
    return response.data;
};

/**
 * Spécifique (Lac des Graves) — envoi e-mails observation-triggered en masse.
 * GET /api/trips/observation-triggered/bulk-send-email
 */
export const sendObservationTriggeredBulkEmail = async () => {
    const response = await apiClient.get(
        'api/trips/observation-triggered/bulk-send-email',
        { timeout: 0, skipCache: true },
    );
    return response.data;
};

/**
 * Envoie les emails des forecasts déclenchés via le traitement backup ECMWF.
 * La date filtre Forecast.trip_day (aujourd'hui Europe/Paris par défaut).
 */
export const sendBackupForecastTriggeredBulkEmail = async ({
    trip_day,
    partner_names = [],
} = {}) => {
    const queryParams = new URLSearchParams();
    if (trip_day && String(trip_day).trim() !== '') {
        queryParams.append('trip_day', String(trip_day).trim());
    }
    if (Array.isArray(partner_names)) {
        partner_names
            .filter((name) => typeof name === 'string' && name.trim() !== '')
            .forEach((name) => queryParams.append('partner_names', name.trim()));
    }
    const queryString = queryParams.toString();
    const url = queryString
        ? `api/backup_ecmwf/trips/forecast-triggered/bulk-send-email?${queryString}`
        : 'api/backup_ecmwf/trips/forecast-triggered/bulk-send-email';
    const response = await apiClient.get(url, { timeout: 0, skipCache: true });
    return response.data;
};

/**
 * Backup Standard autonome — e-mails client de déclenchement forecast.
 * GET /api/standard/trips/forecasts-triggered/backup-send-email
 * Filtre Forecast.trip_day (pas forecast_last_modified_at). Pas d'import GRIB.
 * @param {string} [trip_day]
 * @param {string[]} [partner_names]
 */
export const sendStandardForecastTriggeredBackupEmails = async ({
    trip_day,
    partner_names = [],
} = {}) => {
    const queryParams = new URLSearchParams();
    if (trip_day && String(trip_day).trim() !== '') {
        queryParams.append('trip_day', String(trip_day).trim());
    }
    if (Array.isArray(partner_names)) {
        partner_names
            .filter((name) => typeof name === 'string' && name.trim() !== '')
            .forEach((name) => queryParams.append('partner_names', name.trim()));
    }
    const queryString = queryParams.toString();
    const url = queryString
        ? `api/standard/trips/forecasts-triggered/backup-send-email?${queryString}`
        : 'api/standard/trips/forecasts-triggered/backup-send-email';
    const response = await apiClient.get(url, { timeout: 0, skipCache: true });
    return response.data;
};

/**
 * Backup Standard autonome — e-mails client « pas de déclenchement » forecast.
 * GET /api/standard/trips/forecasts-not-triggered/backup-send-email
 * Filtre Forecast.trip_day (pas forecast_last_modified_at). Pas d'import GRIB.
 * @param {string} [trip_day]
 * @param {string[]} [partner_names]
 */
export const sendStandardForecastNotTriggeredBackupEmails = async ({
    trip_day,
    partner_names = [],
} = {}) => {
    const queryParams = new URLSearchParams();
    if (trip_day && String(trip_day).trim() !== '') {
        queryParams.append('trip_day', String(trip_day).trim());
    }
    if (Array.isArray(partner_names)) {
        partner_names
            .filter((name) => typeof name === 'string' && name.trim() !== '')
            .forEach((name) => queryParams.append('partner_names', name.trim()));
    }
    const queryString = queryParams.toString();
    const url = queryString
        ? `api/standard/trips/forecasts-not-triggered/backup-send-email?${queryString}`
        : 'api/standard/trips/forecasts-not-triggered/backup-send-email';
    const response = await apiClient.get(url, { timeout: 0, skipCache: true });
    return response.data;
};

/**
 * Envoie les SMS des forecasts déclenchés via le traitement backup ECMWF.
 * La date filtre Forecast.trip_day (aujourd'hui Europe/Paris par défaut).
 */
export const sendBackupForecastTriggeredBulkSms = async ({
    trip_day,
    partner_names = [],
} = {}) => {
    const queryParams = new URLSearchParams();
    if (trip_day && String(trip_day).trim() !== '') {
        queryParams.append('trip_day', String(trip_day).trim());
    }
    if (Array.isArray(partner_names)) {
        partner_names
            .filter((name) => typeof name === 'string' && name.trim() !== '')
            .forEach((name) => queryParams.append('partner_names', name.trim()));
    }
    const queryString = queryParams.toString();
    const url = queryString
        ? `api/backup_ecmwf/trips/forecast-triggered/bulk-send-sms?${queryString}`
        : 'api/backup_ecmwf/trips/forecast-triggered/bulk-send-sms';
    const response = await apiClient.get(url, { timeout: 0, skipCache: true });
    return response.data;
};

/**
 * Import Excel ECMWF V2 des prévisions planifiées.
 * Le navigateur définit lui-même le Content-Type multipart et sa boundary.
 */
export const importEcmwfV2ScheduledForecastExcel = async (file, dryRun = true, signal) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(
        `api/ecmwf-v2/trips/scheduled-forecast/import-excel?dry_run=${dryRun ? 'true' : 'false'}`,
        formData,
        {
            timeout: 0,
            signal,
        }
    );
    return response.data;
};

/**
 * Import Excel Standard des prévisions planifiées (scheduled-forecast).
 * POST /api/standard/trips/scheduled-forecast/import-excel
 * Colonne Excel obligatoire `data_source` (ECMWF | METEOMATICS).
 * Indépendant de l'import ECMWF V2.
 */
export const importStandardScheduledForecastExcel = async (file, dryRun = true, signal) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(
        `api/standard/trips/scheduled-forecast/import-excel?dry_run=${dryRun ? 'true' : 'false'}`,
        formData,
        {
            timeout: 0,
            signal,
        }
    );
    return response.data;
};

/** Preview suppression bulk check forecast pour un trip_day (policy_code optionnel). */
export const getCheckForecastBulkDeletePreview = async ({ tripDay, policyCode, signal } = {}) => {
    const params = { trip_day: String(tripDay).trim() };
    const code = String(policyCode || '').trim();
    if (code) params.policy_code = code;
    const response = await apiClient.get('api/check-forecast/bulk-delete-preview', {
        params,
        skipCache: true,
        signal,
    });
    return response.data;
};

/** Suppression bulk check forecast (confirm=true obligatoire, policy_code optionnel). */
export const executeCheckForecastBulkDelete = async ({ tripDay, policyCode, signal } = {}) => {
    const params = {
        trip_day: String(tripDay).trim(),
        confirm: true,
    };
    const code = String(policyCode || '').trim();
    if (code) params.policy_code = code;
    const response = await apiClient.delete('api/check-forecast/bulk-delete', {
        params,
        skipCache: true,
        signal,
    });
    return response.data;
};

/** Preview suppression bulk check forecast via fichier import. */
export const getCheckForecastBulkDeleteImportPreview = async (file, signal) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post('api/check-forecast/bulk-delete-import/preview', formData, {
        skipCache: true,
        timeout: 0,
        signal,
    });
    return response.data;
};

/** Suppression bulk check forecast via fichier import (confirm=true obligatoire). */
export const executeCheckForecastBulkDeleteImport = async (file, signal) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(
        'api/check-forecast/bulk-delete-import?confirm=true',
        formData,
        {
            skipCache: true,
            timeout: 0,
            signal,
        },
    );
    return response.data;
};

// Flower — récap email coordonnées bancaires / remboursement (trips claims)
export const sendTripsClientsBankDetailsRecapEmail = async (params = {}) => {
    const { trip_end_date_from, trip_end_date_to, company_name } = params;
    const queryParams = new URLSearchParams();
    if (trip_end_date_from) queryParams.append('trip_end_date_from', trip_end_date_from);
    if (trip_end_date_to) queryParams.append('trip_end_date_to', trip_end_date_to);
    const companyNames = Array.isArray(company_name)
        ? company_name
        : company_name != null && String(company_name).trim() !== ''
            ? [String(company_name).trim()]
            : [];
    companyNames
        .map((n) => String(n).trim())
        .filter((n) => n !== '')
        .forEach((n) => queryParams.append('company_name', n));
    const qs = queryParams.toString();
    const url = qs
        ? `api/trips/clients/bank-details/send-triggered-recap-email?${qs}`
        : 'api/trips/clients/bank-details/send-triggered-recap-email';
    try {
        const response = await apiClient.get(url, { timeout: 0 });
        return response.data;
    } catch (error) {
        if (error?.response?.data?.code) {
            return error.response.data;
        }
        throw error;
    }
};

/** Relances e-mail remboursement / coordonnées bancaires — par période (POST query) */
export const sendTripsClientsBankDetailsRemindersEmail = async (params = {}) => {
    const { reminder_date_start, reminder_date_end, company_name } = params;
    const queryParams = new URLSearchParams();
    if (reminder_date_start) queryParams.append('reminder_date_start', reminder_date_start);
    if (reminder_date_end) queryParams.append('reminder_date_end', reminder_date_end);
    if (company_name != null && String(company_name).trim() !== '') {
        queryParams.append('company_name', String(company_name).trim());
    }
    const qs = queryParams.toString();
    const url = qs
        ? `api/trips/clients/bank-details/send-reminders-email?${qs}`
        : 'api/trips/clients/bank-details/send-reminders-email';
    const response = await apiClient.post(url, {}, { timeout: 0 });
    return response.data;
};

/** Relances e-mail remboursement / coordonnées bancaires — upload fichier (POST multipart) */
export const postTripsClientsBankDetailsBulkRemindersEmail = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(
        'api/trips/clients/bank-details/bulk-reminders-email',
        formData,
        {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
            timeout: 300000,
        }
    );
    return response.data;
};

// Aloa Vacances — fin de séjour : aperçu (sans envoi) et notifications e-mail
const buildEndOfStaySendQuery = (params = {}) => {
    const { trip_end_date_from, trip_end_date_to, company_name } = params;
    const queryParams = new URLSearchParams();
    if (trip_end_date_from) queryParams.append('trip_end_date_from', trip_end_date_from);
    if (trip_end_date_to) queryParams.append('trip_end_date_to', trip_end_date_to);
    if (company_name != null && String(company_name).trim() !== '') {
        queryParams.append('company_name', String(company_name).trim());
    }
    return queryParams.toString();
};

const postStructuredRecapEmail = async (path, queryString) => {
    const url = queryString ? `${path}?${queryString}` : path;
    try {
        const response = await apiClient.post(url, {}, { timeout: 0 });
        return response.data;
    } catch (error) {
        if (error?.response?.data?.code) {
            return error.response.data;
        }
        throw error;
    }
};

const buildAloaEndOfStayQuery = (params = {}) => {
    const { trip_end_date_from, trip_end_date_to } = params;
    const queryParams = new URLSearchParams();
    if (trip_end_date_from) queryParams.append('trip_end_date_from', trip_end_date_from);
    if (trip_end_date_to) queryParams.append('trip_end_date_to', trip_end_date_to);
    return queryParams.toString();
};

export const getAloaEndOfStayPreview = async (params = {}) => {
    const qs = buildAloaEndOfStayQuery(params);
    const url = qs
        ? `api/trips/aloa/policies/end-of-stay-preview?${qs}`
        : 'api/trips/aloa/policies/end-of-stay-preview';
    const response = await apiClient.get(url);
    return response.data;
};

export const sendAloaEndOfStayNotifications = async (params = {}) => {
    const qs = buildEndOfStaySendQuery(params);
    return postStructuredRecapEmail(
        'api/trips/clients/bank-details/send-triggered-and-not-triggered-recap-email',
        qs
    );
};

// Lac des Graves — fin de séjour : aperçu (sans envoi) et récap déclenché / non déclenché + RIB
const buildLacdesgravesEndOfStayQuery = (params = {}) => {
    const { trip_end_date_from, trip_end_date_to } = params;
    const queryParams = new URLSearchParams();
    if (trip_end_date_from) queryParams.append('trip_end_date_from', trip_end_date_from);
    if (trip_end_date_to) queryParams.append('trip_end_date_to', trip_end_date_to);
    return queryParams.toString();
};

export const getLacdesgravesEndOfStayPreview = async (params = {}) => {
    const qs = buildLacdesgravesEndOfStayQuery(params);
    const url = qs
        ? `api/trips/lacdesgraves/policies/end-of-stay-preview?${qs}`
        : 'api/trips/lacdesgraves/policies/end-of-stay-preview';
    const response = await apiClient.get(url);
    return response.data;
};

export const sendLacdesgravesEndOfStayRecapEmail = async (params = {}) => {
    const qs = buildEndOfStaySendQuery(params);
    return postStructuredRecapEmail(
        'api/trips/lacdesgraves/clients/bank-details/send-triggered-and-not-triggered-recap-email',
        qs
    );
};

/** Standard — récap fin de séjour déclenché / non déclenché (multi-partenaires, Admin uniquement) */
export const sendStandardEndOfStayRecapEmail = async (params = {}) => {
    const qs = buildEndOfStaySendQuery(params);
    return postStructuredRecapEmail(
        'api/standard/trips/clients/bank-details/send-triggered-and-not-triggered-recap-email',
        qs
    );
};

/** Standard — relances e-mail RIB (pipeline autonome, Admin uniquement) */
export const sendStandardTripsClientsBankDetailsRemindersEmail = async (params = {}) => {
    const { reminder_date_start, reminder_date_end, company_name } = params;
    const queryParams = new URLSearchParams();
    if (reminder_date_start) queryParams.append('reminder_date_start', reminder_date_start);
    if (reminder_date_end) queryParams.append('reminder_date_end', reminder_date_end);
    if (company_name != null && String(company_name).trim() !== '') {
        queryParams.append('company_name', String(company_name).trim());
    }
    const qs = queryParams.toString();
    const url = qs
        ? `api/standard/trips/clients/bank-details/send-reminders-email?${qs}`
        : 'api/standard/trips/clients/bank-details/send-reminders-email';
    try {
        const response = await apiClient.post(url, {}, { timeout: 0 });
        return response.data;
    } catch (error) {
        if (error?.response?.data?.code) {
            return error.response.data;
        }
        throw error;
    }
};

/**
 * Standard — check forecast planifié (import + bulk check claims + e-mail récap admin).
 * GET /api/standard/trips/check-forecasts
 * La source forecast est lue côté API depuis subcoverage_trip.forecast_data_source.
 */
export const checkStandardTripsForecasts = async () => {
    const response = await apiClient.get(
        'api/standard/trips/check-forecasts',
        { timeout: 0, skipCache: true },
    );
    return response.data;
};

/**
 * Standard — check forecast planifié via source fallback.
 * GET /api/standard/trips/fallback/check-forecasts
 * La source forecast est lue côté API depuis subcoverage_trip.forecast_data_source_fallback.
 */
export const checkStandardFallbackTripsForecasts = async () => {
    const response = await apiClient.get(
        'api/standard/trips/fallback/check-forecasts',
        { timeout: 0, skipCache: true },
    );
    return response.data;
};

/**
 * Standard — bulk check observation groupé par localisation + obs_data_source.
 * POST /api/standard/trips/scheduled/bulk-observation-check/grouped-by-location
 * Filtre trigger_type=observation ; sources METEOSTAT | METEOFRANCE depuis SubCoverageTrip.obs_data_source.
 */
export const checkStandardScheduledBulkObservations = async () => {
    const response = await apiClient.post(
        'api/standard/trips/scheduled/bulk-observation-check/grouped-by-location',
        null,
        { timeout: 0, skipCache: true },
    );
    return response.data;
};

/**
 * Standard — bulk check observation FALLBACK groupé par localisation + obs_data_source_fallback.
 * POST /api/standard/trips/scheduled/bulk-observation-check/fallback/grouped-by-location
 * Pipeline indépendant du bulk primaire ; sources METEOSTAT | METEOFRANCE depuis SubCoverageTrip.obs_data_source_fallback.
 */
export const checkStandardScheduledBulkObservationFallback = async () => {
    const response = await apiClient.post(
        'api/standard/trips/scheduled/bulk-observation-check/fallback/grouped-by-location',
        null,
        { timeout: 0, skipCache: true },
    );
    return response.data;
};

/**
 * Standard — envoi e-mails client de déclenchement forecast.
 * GET /api/standard/trips/forecasts-triggered/send-email
 * @param {string} [companyName] Filtre optionnel User.company_name (exact, insensible à la casse).
 */
export const sendStandardForecastTriggeredEmails = async (companyName) => {
    const queryParams = new URLSearchParams();
    if (companyName != null && String(companyName).trim() !== '') {
        queryParams.append('company_name', String(companyName).trim());
    }
    const queryString = queryParams.toString();
    const url = queryString
        ? `api/standard/trips/forecasts-triggered/send-email?${queryString}`
        : 'api/standard/trips/forecasts-triggered/send-email';
    const response = await apiClient.get(url, { timeout: 0, skipCache: true });
    return response.data;
};

/**
 * Standard — envoi e-mails client de déclenchement observation.
 * POST /api/standard/trips/observations-triggered/send-email
 * Pipeline : check observation groupé (trigger_type=observation), puis e-mails
 * MessageResultatDeclencheobs. Ne traite que is_triggered_observation=True.
 * @param {string} [companyName] Filtre optionnel User.company_name (exact, insensible à la casse).
 */
export const sendStandardObservationTriggeredEmails = async (companyName) => {
    const queryParams = new URLSearchParams();
    if (companyName != null && String(companyName).trim() !== '') {
        queryParams.append('company_name', String(companyName).trim());
    }
    const queryString = queryParams.toString();
    const url = queryString
        ? `api/standard/trips/observations-triggered/send-email?${queryString}`
        : 'api/standard/trips/observations-triggered/send-email';
    const response = await apiClient.post(url, null, { timeout: 0, skipCache: true });
    return response.data;
};

/**
 * Standard — envoi e-mails client « pas de déclenchement » observation.
 * POST /api/standard/trips/observations-not-triggered/send-email
 * Pipeline : check observation groupé (trigger_type=observation), puis e-mails
 * MessageResultatNonDeclencheobs. Ne traite que is_triggered_observation=False.
 * @param {string} [companyName] Filtre optionnel User.company_name (exact, insensible à la casse).
 */
export const sendStandardObservationNotTriggeredEmails = async (companyName) => {
    const queryParams = new URLSearchParams();
    if (companyName != null && String(companyName).trim() !== '') {
        queryParams.append('company_name', String(companyName).trim());
    }
    const queryString = queryParams.toString();
    const url = queryString
        ? `api/standard/trips/observations-not-triggered/send-email?${queryString}`
        : 'api/standard/trips/observations-not-triggered/send-email';
    const response = await apiClient.post(url, null, { timeout: 0, skipCache: true });
    return response.data;
};

/**
 * Standard — envoi e-mails client « pas de déclenchement » forecast.
 * GET /api/standard/trips/forecasts-not-triggered/send-email
 * @param {string} [companyName] Filtre optionnel User.company_name (exact, insensible à la casse).
 */
export const sendStandardForecastNotTriggeredEmails = async (companyName) => {
    const queryParams = new URLSearchParams();
    if (companyName != null && String(companyName).trim() !== '') {
        queryParams.append('company_name', String(companyName).trim());
    }
    const queryString = queryParams.toString();
    const url = queryString
        ? `api/standard/trips/forecasts-not-triggered/send-email?${queryString}`
        : 'api/standard/trips/forecasts-not-triggered/send-email';
    const response = await apiClient.get(url, { timeout: 0, skipCache: true });
    return response.data;
};

// API Admin Users
export const getAdminUsers = async () => {
    const response = await apiClient.get('users');
    return response.data;
};

export const getAdminUserById = async (userId) => {
    const response = await apiClient.get(`users/${userId}`);
    return response.data;
};

export const deleteAdminUser = async (userId) => {
    const response = await apiClient.delete(`users/${userId}`);
    return response.data;
};

export const updateAdminUser = async (userId, userData) => {
    const response = await apiClient.put(`users/${userId}`, userData);
    return response.data;
};

export const createAdminAccount = async (userData) => {
    const response = await apiClient.post('admin-accounts/create', userData);
    return response.data;
};

// API Claims
const TRIPS_CLAIMS_FILTER_KEYS = [
    'reservation_number',
    'policy_code',
    'client_email',
    'claim_code',
    'date_of_claim',
    'claim_status',
    'activity_end_date_from',
    'activity_end_date_to',
    'company_name',
    'trip_modified',
    'new_trip_start_date_from',
    'new_trip_start_date_to',
    'new_trip_end_date_from',
    'new_trip_end_date_to',
    'new_trip_effective_start_date_from',
    'new_trip_effective_start_date_to',
    'new_trip_effective_end_date_from',
    'new_trip_effective_end_date_to',
];

const appendTripsClaimsFilters = (queryParams, params = {}) => {
    TRIPS_CLAIMS_FILTER_KEYS.forEach((key) => {
        const value = params[key];
        if (value == null || value === '') return;
        if (key === 'trip_modified') {
            if (value === true || value === 'true' || value === false || value === 'false') {
                queryParams.append(key, String(value));
            }
            return;
        }
        const trimmed = String(value).trim();
        if (trimmed !== '') {
            queryParams.append(key, trimmed);
        }
    });
};

export const getClaimsTrips = async (params = {}) => {
    const { skip = 0, limit = 10 } = params;

    const queryParams = new URLSearchParams();
    queryParams.append('skip', skip.toString());
    queryParams.append('limit', limit.toString());
    appendTripsClaimsFilters(queryParams, params);

    const response = await apiClient.get(`api/pagination/trips/claims?${queryParams.toString()}`);
    return response.data;
};

/** Liste complète des claims trips (sans pagination), mêmes filtres optionnels que la pagination */
export const getTripsClaimsFullList = async (params = {}) => {
    const queryParams = new URLSearchParams();
    appendTripsClaimsFilters(queryParams, params);

    const qs = queryParams.toString();
    const url = qs ? `api/trips/claims?${qs}` : 'api/trips/claims';
    const response = await apiClient.get(url, { timeout: 120000 });
    return response.data;
};

export const getClaimsTripsB2C = async (params = {}) => {
    const {
        skip = 0,
        limit = 10,
        reservation_number,
        policy_code,
        client_email,
        activity_end_date_from,
        activity_end_date_to,
        claim_code,
        date_of_claim,
        company_name,
    } = params;

    const queryParams = new URLSearchParams();
    queryParams.append('skip', skip.toString());
    queryParams.append('limit', limit.toString());

    if (reservation_number) queryParams.append('reservation_number', reservation_number);
    if (policy_code) queryParams.append('policy_code', policy_code);
    if (client_email) queryParams.append('client_email', client_email);
    if (activity_end_date_from) queryParams.append('activity_end_date_from', activity_end_date_from);
    if (activity_end_date_to) queryParams.append('activity_end_date_to', activity_end_date_to);
    if (claim_code) queryParams.append('claim_code', claim_code);
    if (date_of_claim) queryParams.append('date_of_claim', date_of_claim);
    if (company_name && String(company_name).trim() !== '') {
        queryParams.append('company_name', String(company_name).trim());
    }

    const response = await apiClient.get(`api/pagination/trips/claims/b2c?${queryParams.toString()}`);
    return response.data;
};

export const getTripsClaimsToday = async (params = {}) => {
    const {
        skip = 0,
        limit = 10,
    } = params;

    const queryParams = new URLSearchParams();
    queryParams.append('skip', skip.toString());
    queryParams.append('limit', limit.toString());

    const response = await apiClient.get(`api/pagination/trips/claims/today?${queryParams.toString()}`);
    return response.data;
};

export const getTripsCheckClaims = async (params = {}) => {
    const {
        skip = 0,
        limit = 10,
        reservation_number,
        policy_code,
        client_email,
        activity_end_date_from,
        activity_end_date_to,
        claim_code,
        date_of_claim,
        trip_day,
        company_name,
        reference_name,
    } = params;

    const queryParams = new URLSearchParams();
    queryParams.append('skip', skip.toString());
    queryParams.append('limit', limit.toString());

    if (reservation_number) queryParams.append('reservation_number', reservation_number);
    if (policy_code) queryParams.append('policy_code', policy_code);
    if (client_email) queryParams.append('client_email', client_email);
    if (activity_end_date_from) queryParams.append('activity_end_date_from', activity_end_date_from);
    if (activity_end_date_to) queryParams.append('activity_end_date_to', activity_end_date_to);
    if (claim_code) queryParams.append('claim_code', claim_code);
    if (date_of_claim) queryParams.append('date_of_claim', date_of_claim);
    if (trip_day) queryParams.append('trip_day', trip_day);
    if (company_name) queryParams.append('company_name', company_name);
    if (reference_name) queryParams.append('reference_name', reference_name);

    const response = await apiClient.get(`api/pagination/trips/check-claims?${queryParams.toString()}`);
    return response.data;
};

/** Liste admin complète check claims (filtres optionnels, sans pagination) — GET /trips/check-claims */
const TRIPS_CHECK_CLAIMS_FULL_FILTER_KEYS = [
    'policy_code',
    'status',
    'reservation_number',
    'company_name',
    'client_first_name',
    'client_last_name',
    'client_email',
    'activity_start_date_from',
    'activity_start_date_to',
    'activity_end_date_from',
    'activity_end_date_to',
    'trip_modified',
    'new_trip_start_date_from',
    'new_trip_start_date_to',
    'new_trip_end_date_from',
    'new_trip_end_date_to',
    'new_trip_effective_start_date_from',
    'new_trip_effective_start_date_to',
    'new_trip_effective_end_date_from',
    'new_trip_effective_end_date_to',
    'trip_day',
];

export const getAdminTripsCheckClaimsFullList = async (params = {}) => {
    const queryParams = new URLSearchParams();
    TRIPS_CHECK_CLAIMS_FULL_FILTER_KEYS.forEach((key) => {
        const value = params[key];
        if (value == null || value === '') return;
        if (key === 'trip_modified') {
            if (value === true || value === 'true' || value === false || value === 'false') {
                queryParams.append(key, String(value));
            }
            return;
        }
        const trimmed = String(value).trim();
        if (trimmed !== '') {
            queryParams.append(key, trimmed);
        }
    });

    const qs = queryParams.toString();
    const url = qs ? `api/trips/check-claims?${qs}` : 'api/trips/check-claims';
    const response = await apiClient.get(url, { timeout: 120000 });
    return response.data;
};

export const getClaims = async (params = {}) => {
    const {
        skip = 0,
        limit = 10,
        reservation_number,
        policy_code,
        client_email,
        claim_code,
        date_of_claim,
        activity_end_date_from,
        activity_end_date_to,
        company_name,
    } = params;

    const queryParams = new URLSearchParams();
    queryParams.append('skip', skip.toString());
    queryParams.append('limit', limit.toString());

    if (reservation_number) queryParams.append('reservation_number', reservation_number);
    if (policy_code) queryParams.append('policy_code', policy_code);
    if (client_email) queryParams.append('client_email', client_email);
    if (claim_code) queryParams.append('claim_code', claim_code);
    if (date_of_claim) queryParams.append('date_of_claim', date_of_claim);
    if (activity_end_date_from) queryParams.append('activity_end_date_from', activity_end_date_from);
    if (activity_end_date_to) queryParams.append('activity_end_date_to', activity_end_date_to);
    if (company_name && String(company_name).trim() !== '') {
        queryParams.append('company_name', String(company_name).trim());
    }

    const response = await apiClient.get(`api/pagination/claims?${queryParams.toString()}`);
    return response.data;
};

/** Liste complète des sinistres activités (sans pagination) — GET /claims, mêmes filtres optionnels que la pagination */
export const getActivityClaimsFullList = async (params = {}) => {
    const {
        reservation_number,
        policy_code,
        client_email,
        claim_code,
        date_of_claim,
        activity_end_date_from,
        activity_end_date_to,
        company_name,
    } = params;

    const queryParams = new URLSearchParams();
    if (reservation_number) queryParams.append('reservation_number', reservation_number);
    if (policy_code) queryParams.append('policy_code', policy_code);
    if (client_email) queryParams.append('client_email', client_email);
    if (claim_code) queryParams.append('claim_code', claim_code);
    if (date_of_claim) queryParams.append('date_of_claim', date_of_claim);
    if (activity_end_date_from) queryParams.append('activity_end_date_from', activity_end_date_from);
    if (activity_end_date_to) queryParams.append('activity_end_date_to', activity_end_date_to);
    if (company_name && String(company_name).trim() !== '') {
        queryParams.append('company_name', String(company_name).trim());
    }

    const qs = queryParams.toString();
    const url = qs ? `api/claims?${qs}` : 'api/claims';
    const response = await apiClient.get(url, { timeout: 120000 });
    return response.data;
};

export const getCheckClaims = async (params = {}) => {
    const {
        skip = 0,
        limit = 10,
        reservation_number,
        policy_code,
        client_email,
        claim_code,
        date_of_claim,
        activity_end_date_from,
        activity_end_date_to,
    } = params;

    const queryParams = new URLSearchParams();
    queryParams.append('skip', skip.toString());
    queryParams.append('limit', limit.toString());

    if (reservation_number) queryParams.append('reservation_number', reservation_number);
    if (policy_code) queryParams.append('policy_code', policy_code);
    if (client_email) queryParams.append('client_email', client_email);
    if (claim_code) queryParams.append('claim_code', claim_code);
    if (date_of_claim) queryParams.append('date_of_claim', date_of_claim);
    if (activity_end_date_from) queryParams.append('activity_end_date_from', activity_end_date_from);
    if (activity_end_date_to) queryParams.append('activity_end_date_to', activity_end_date_to);

    const response = await apiClient.get(`api/pagination/check-claims?${queryParams.toString()}`);
    return response.data;
};

export const getTripClaimById = async (claimId) => {
    const response = await apiClient.get(`api/trips/claims/${claimId}`);
    return response.data;
};

export const patchTripClaimStatus = async (claimId, payload) => {
    const response = await apiClient.patch(`api/trips/claims/${claimId}/status`, payload);
    return response.data;
};

export const getActivityClaimById = async (claimId) => {
    const response = await apiClient.get(`api/claims/${claimId}`);
    return response.data;
};

export const generateClaimTokenInfo = async (claimId) => {
    const response = await apiClient.get(`api/claims/generate-token-info/${claimId}`);
    return response.data;
};

export const generateActivityConfirmationLink = async (policyCode) => {
    const response = await apiClient.post(`api/claims/confirm-activity-link/${policyCode}`);
    return response.data;
};

export const generateForecastTriggeredUrl = async (claimId) => {
    const response = await apiClient.post(`api/claims/forecasts/generate-triggered-url/${claimId}`);
    return response.data;
};

export const generateActivityConfirmationToken = async (claimId) => {
    const response = await apiClient.post(`api/claims/generate-activity-confirmation/${claimId}`);
    return response.data;
};

export const generatePaymentLink = async (policyCode) => {
    const response = await apiClient.post(`api/clients/generate-payment-link/${policyCode}`);
    return response.data;
};

export const updateActivityStatus = async (claimId, activityStatus) => {
    const response = await apiClient.put(`api/activities/${claimId}/activity-status`, {
        activity_status: activityStatus
    });
    return response.data;
};

export const updateClaimStatus = async (claimId, claimStatusData = {}) => {
    const body = {};
    const queryParams = new URLSearchParams();
    const { claim_status, payment_date, payment_mode } = claimStatusData;

    const appendField = (key, value) => {
        if (value == null || String(value).trim() === '') return;
        const trimmed = String(value).trim();
        body[key] = trimmed;
        queryParams.append(key, trimmed);
    };

    appendField('claim_status', claim_status);
    appendField('payment_date', payment_date);
    appendField('payment_mode', payment_mode);

    const qs = queryParams.toString();
    const url = qs
        ? `api/claims/${claimId}/change-status?${qs}`
        : `api/claims/${claimId}/change-status`;

    const response = await apiClient.put(url, body, {
        headers: { 'Content-Type': 'application/json' },
    });
    return response.data;
};

export const confirmActivityStatus = async (claimId) => {
    const response = await apiClient.put(`api/claims/${claimId}/confirm-activity-status`);
    return response.data;
};

export const checkMeteoFranceObservations = async (claimId) => {
    const response = await apiClient.put(`api/claims/meteofrance/check-observations/${claimId}`);
    return response.data;
};

export const checkMeteoFranceObservationsForForecastNotTriggered = async (forecastNotTriggeredId) => {
    const response = await apiClient.put(`api/forecast-not-triggered/meteofrance/check-observations/${forecastNotTriggeredId}`);
    return response.data;
};

export const checkGreenRiverMeteoFranceObservationsForForecastNotTriggered = async (forecastNotTriggeredId) => {
    const response = await apiClient.put(`api/green-river/forecast-not-triggered/meteofrance/check-observations/${forecastNotTriggeredId}`);
    return response.data;
};

export const checkMeteostatObservationsForForecastNotTriggered = async (forecastNotTriggeredId) => {
    const response = await apiClient.put(`api/forecast-not-triggered/meteostat/check-observations/${forecastNotTriggeredId}`);
    return response.data;
};

export const checkGreenRiverMeteostatObservationsForForecastNotTriggered = async (forecastNotTriggeredId) => {
    const response = await apiClient.put(`api/green-river/forecast-not-triggered/meteostat/check-observations/${forecastNotTriggeredId}`);
    return response.data;
};

export const createOrUpdateMeteostatObservations = async (claimId) => {
    const response = await apiClient.put(`api/claims/observations/${claimId}`);
    return response.data;
};

export const createEcmwfForecast = async (policyId) => {
    const response = await apiClient.post(`api/ecmwf/forecast/${policyId}`);
    return response.data;
};

export const getCheckClaimById = async (checkClaimId) => {
    const response = await apiClient.get(`api/check-claims/${checkClaimId}`);
    return response.data;
};

export const deleteCheckClaim = async (checkClaimId) => {
    const response = await apiClient.delete(`api/check-claims/${checkClaimId}`);
    return response.data;
};

export const createCheckClaimFromForecast = async (forecastId) => {
    const response = await apiClient.post(`api/check-claims/${forecastId}`);
    return response.data;
};

export const getTripsCheckClaimById = async (checkClaimId) => {
    const response = await apiClient.get(`api/trips/check-claims/${checkClaimId}`);
    return response.data;
};

export const deleteClaimById = async (claimId) => {
    const response = await apiClient.delete(`api/claims/${claimId}`);
    return response.data;
};

export const getClaimsActivities = async () => {
    const response = await apiClient.get('api/claims');
    return response.data;
};

// API Policies
const TRIPS_POLICIES_FILTER_KEYS = [
    'policy_code',
    'reservation_number',
    'client_first_name',
    'client_last_name',
    'client_email',
    'company_name',
    'status',
    'trigger_type',
    'activity_start_date_from',
    'activity_start_date_to',
    'activity_end_date_from',
    'activity_end_date_to',
    'trip_modified',
    'new_trip_start_date_from',
    'new_trip_start_date_to',
    'new_trip_end_date_from',
    'new_trip_end_date_to',
];

const appendTripsPoliciesFilters = (queryParams, params = {}) => {
    TRIPS_POLICIES_FILTER_KEYS.forEach((key) => {
        const value = params[key];
        if (value == null || value === '') return;
        if (key === 'trip_modified') {
            if (value === true || value === 'true' || value === false || value === 'false') {
                queryParams.append(key, String(value));
            }
            return;
        }
        const trimmed = String(value).trim();
        if (trimmed !== '') {
            queryParams.append(key, trimmed);
        }
    });
};

export const getPoliciesTrips = async (params = {}) => {
    const { skip = 0, limit = 10 } = params;

    const queryParams = new URLSearchParams();
    queryParams.append('skip', skip.toString());
    queryParams.append('limit', limit.toString());
    appendTripsPoliciesFilters(queryParams, params);

    const response = await apiClient.get(`api/pagination/trips/policies?${queryParams.toString()}`);
    return response.data;
};

/** Liste admin complète (filtres optionnels, sans pagination) — GET /trips/policies */
export const getAdminTripsPoliciesFullList = async (params = {}) => {
    const queryParams = new URLSearchParams();
    appendTripsPoliciesFilters(queryParams, params);

    const qs = queryParams.toString();
    const url = qs ? `api/trips/policies?${qs}` : 'api/trips/policies';
    const response = await apiClient.get(url, { timeout: 120000 });
    return response.data;
};

export const getPoliciesTripsB2C = async (params = {}) => {
    const {
        skip = 0,
        limit = 10,
        policy_code,
        reservation_number,
        client_first_name,
        client_last_name,
        company_name,
        status,
        activity_start_date_from,
        activity_start_date_to,
        activity_end_date_from,
        activity_end_date_to,
    } = params;

    const queryParams = new URLSearchParams();
    queryParams.append('skip', skip.toString());
    queryParams.append('limit', limit.toString());

    if (policy_code) queryParams.append('policy_code', policy_code);
    if (reservation_number) queryParams.append('reservation_number', reservation_number);
    if (client_first_name) queryParams.append('client_first_name', client_first_name);
    if (client_last_name) queryParams.append('client_last_name', client_last_name);
    if (company_name) queryParams.append('company_name', company_name);
    if (status) queryParams.append('status', status);
    if (activity_start_date_from) queryParams.append('activity_start_date_from', activity_start_date_from);
    if (activity_start_date_to) queryParams.append('activity_start_date_to', activity_start_date_to);
    if (activity_end_date_from) queryParams.append('activity_end_date_from', activity_end_date_from);
    if (activity_end_date_to) queryParams.append('activity_end_date_to', activity_end_date_to);

    const response = await apiClient.get(`api/pagination/trips/policies/b2c?${queryParams.toString()}`);
    return response.data;
};

export const getTripPolicyById = async (policyId) => {
    const response = await apiClient.get(`api/trips/policies/${policyId}`);
    return response.data;
};

export const changePolicyStatus = async (policyId, status) => {
    const response = await apiClient.put(`api/trips/policies/${policyId}/change-status`, { status });
    return response.data;
};

export const generateRibFormUrl = async (policyCode) => {
    const encodedPolicyCode = encodeURIComponent(policyCode);
    const response = await apiClient.get(`api/trips/policies/${encodedPolicyCode}/rib-form-url`);
    return response.data;
};

export const getSubcoveragesTrips = async (params = {}) => {
    console.log('🌐 getSubcoveragesTrips - params reçus:', params);

    const skip = params.skip !== undefined ? params.skip : 0;
    const limit = params.limit !== undefined ? params.limit : 10;
    const company_name = params.company_name;
    const reference_name = params.reference_name;
    const category = params.category;

    const queryParams = new URLSearchParams();
    queryParams.append('skip', skip.toString());
    queryParams.append('limit', limit.toString());

    // Ajouter les filtres seulement s'ils sont définis et non vides
    if (company_name && String(company_name).trim() !== '') {
        queryParams.append('company_name', String(company_name).trim());
        console.log('✅ Filtre company_name ajouté:', company_name);
    }
    if (reference_name && String(reference_name).trim() !== '') {
        queryParams.append('reference_name', String(reference_name).trim());
        console.log('✅ Filtre reference_name ajouté:', reference_name);
    }
    if (category && String(category).trim() !== '') {
        queryParams.append('category', String(category).trim());
        console.log('✅ Filtre category ajouté:', category);
    }

    const url = `api/pagination/subcoverages-trip?${queryParams.toString()}`;
    console.log('🌐 API Request URL complète:', url);
    console.log('🌐 API Request params extraits:', { skip, limit, company_name, reference_name, category });

    const response = await apiClient.get(url);
    console.log('🌐 API Response:', response.data);
    return response.data;
};

export const getSubcoverageTripById = async (subcoverageTripId) => {
    const response = await apiClient.get(`api/subcoverages-trip/${subcoverageTripId}`);
    return response.data;
};

export const getSubcoveragesTripExportByCompanyName = async (companyName) => {
    const queryParams = new URLSearchParams();
    queryParams.append('company_name', String(companyName).trim());
    const response = await apiClient.get(`api/subcoverages-trip/export?${queryParams.toString()}`, {
        timeout: 120000,
    });
    return response.data;
};

export const updateSubcoverageTrip = async (subcoverageTripId, payload) => {
    const response = await apiClient.put(`api/subcoverage-trip/${subcoverageTripId}`, payload);
    return response.data;
};

export const getActivitySubcoverages = async (params = {}) => {
    const {
        skip = 0,
        limit = 10,
        company_name,
        reference_name,
        category,
    } = params;

    const queryParams = new URLSearchParams();
    queryParams.append('skip', skip.toString());
    queryParams.append('limit', limit.toString());

    if (company_name) queryParams.append('company_name', company_name);
    if (reference_name) queryParams.append('reference_name', reference_name);
    if (category) queryParams.append('category', category);

    const response = await apiClient.get(`api/pagination/subcoverages?${queryParams.toString()}`);
    return response.data;
};

export const getActivitySubcoverageById = async (subcoverageId) => {
    const response = await apiClient.get(`api/subcoverages/${subcoverageId}`);
    return response.data;
};

export const getActivitySubcoveragesByPartner = async (partnerId) => {
    const response = await apiClient.get(`api/subcoverages/partner/${partnerId}`);
    return response.data;
};

export const createActivitySubcoverage = async (referenceId, subcoverageData) => {
    const response = await apiClient.post(`api/subcoverage/${referenceId}`, subcoverageData);
    return response.data;
};

export const updateActivitySubcoverage = async (subcoverageId, payload) => {
    const response = await apiClient.put(`api/subcoverage/${subcoverageId}`, payload);
    return response.data;
};

export const deleteActivitySubcoverage = async (subcoverageId) => {
    const response = await apiClient.delete(`api/subcoverage/${subcoverageId}`);
    return response.data;
};

export const getPoliciesActivities = async (params = {}) => {
    const {
        skip = 0,
        limit = 10,
        policy_code,
        reservation_number,
        first_name,
        last_name,
        client_first_name,
        client_last_name,
        client_email,
        company_name,
        status,
        policy_start_date_from,
        policy_start_date_to,
        policy_end_date_from,
        policy_end_date_to,
        activity_start_date_from,
        activity_start_date_to,
        activity_end_date_from,
        activity_end_date_to,
    } = params;

    const queryParams = new URLSearchParams();
    queryParams.append('skip', skip.toString());
    queryParams.append('limit', limit.toString());

    const appendIfPresent = (key, value) => {
        if (value == null || String(value).trim() === '') return;
        queryParams.append(key, String(value).trim());
    };

    appendIfPresent('policy_code', policy_code);
    appendIfPresent('reservation_number', reservation_number);
    appendIfPresent('client_first_name', client_first_name || first_name);
    appendIfPresent('client_last_name', client_last_name || last_name);
    appendIfPresent('client_email', client_email);
    appendIfPresent('company_name', company_name);
    appendIfPresent('status', status);
    appendIfPresent('activity_start_date_from', activity_start_date_from || policy_start_date_from);
    appendIfPresent('activity_start_date_to', activity_start_date_to || policy_start_date_to);
    appendIfPresent('activity_end_date_from', activity_end_date_from || policy_end_date_from);
    appendIfPresent('activity_end_date_to', activity_end_date_to || policy_end_date_to);

    const response = await apiClient.get(`api/pagination/policies?${queryParams.toString()}`);
    return response.data;
};

/** Liste admin complète polices activités (filtres optionnels, sans pagination) — GET /policies */
export const getAdminActivitiesPoliciesFullList = async (params = {}) => {
    const {
        policy_code,
        reservation_number,
        client_first_name,
        client_last_name,
        company_name,
        status,
        activity_start_date_from,
        activity_start_date_to,
        activity_end_date_from,
        activity_end_date_to,
    } = params;

    const queryParams = new URLSearchParams();
    if (policy_code) queryParams.append('policy_code', policy_code);
    if (reservation_number) queryParams.append('reservation_number', reservation_number);
    if (client_first_name) queryParams.append('client_first_name', client_first_name);
    if (client_last_name) queryParams.append('client_last_name', client_last_name);
    if (company_name) queryParams.append('company_name', company_name);
    if (status) queryParams.append('status', status);
    if (activity_start_date_from) queryParams.append('activity_start_date_from', activity_start_date_from);
    if (activity_start_date_to) queryParams.append('activity_start_date_to', activity_start_date_to);
    if (activity_end_date_from) queryParams.append('activity_end_date_from', activity_end_date_from);
    if (activity_end_date_to) queryParams.append('activity_end_date_to', activity_end_date_to);

    const qs = queryParams.toString();
    const url = qs ? `api/policies?${qs}` : 'api/policies';
    const response = await apiClient.get(url, { timeout: 120000 });
    return response.data;
};

export const getActivityPolicyById = async (policyId) => {
    const response = await apiClient.get(`api/policies/${policyId}`);
    return response.data;
};

export const changeActivityPolicyStatus = async (policyId, status) => {
    const response = await apiClient.put(`api/policies/${policyId}/change-status`, { status });
    return response.data;
};

export const deleteActivityPolicy = async (policyId) => {
    const response = await apiClient.delete(`api/policies/${policyId}`);
    return response.data;
};

export const getPoliciesStartingToday = async (filterDate = null) => {
    let url = 'api/policies/start/today';
    if (filterDate) {
        const queryParams = new URLSearchParams();
        queryParams.append('filter_date', filterDate);
        url += `?${queryParams.toString()}`;
    }
    const response = await apiClient.get(url);
    return response.data;
};

// API Trips (liste générale)
const TRIPS_FILTER_KEYS = [
    'category',
    'name',
    'activity_start_date_from',
    'activity_start_date_to',
    'activity_end_date_from',
    'activity_end_date_to',
    'trip_modified',
    'new_trip_start_date_from',
    'new_trip_start_date_to',
    'new_trip_end_date_from',
    'new_trip_end_date_to',
    'new_trip_effective_start_date_from',
    'new_trip_effective_start_date_to',
    'new_trip_effective_end_date_from',
    'new_trip_effective_end_date_to',
    'company_name',
    'activity_code',
    'reservation_number',
];

const appendTripsFilters = (queryParams, params = {}) => {
    TRIPS_FILTER_KEYS.forEach((key) => {
        const value = params[key];
        if (value == null || value === '') return;
        if (key === 'trip_modified') {
            if (value === true || value === 'true' || value === false || value === 'false') {
                queryParams.append(key, String(value));
            }
            return;
        }
        const trimmed = String(value).trim();
        if (trimmed !== '') {
            queryParams.append(key, trimmed);
        }
    });
};

export const getTrips = async (params = {}) => {
    const { skip = 0, limit = 10 } = params;

    const queryParams = new URLSearchParams();
    queryParams.append('skip', skip.toString());
    queryParams.append('limit', limit.toString());
    appendTripsFilters(queryParams, params);

    const response = await apiClient.get(`api/pagination/trips?${queryParams.toString()}`);
    return response.data;
};

export const getTripsObservations = async (params = {}) => {
    const {
        skip = 0,
        limit = 10,
        policy_code,
        activity_start_date,
        trip_day,
        reservation_number,
        client_first_name,
        client_last_name,
    } = params;

    const queryParams = new URLSearchParams();
    queryParams.append('skip', skip.toString());
    queryParams.append('limit', limit.toString());

    if (policy_code) queryParams.append('policy_code', policy_code);
    if (activity_start_date) queryParams.append('activity_start_date', activity_start_date);
    if (trip_day) queryParams.append('trip_day', trip_day);
    if (reservation_number) queryParams.append('reservation_number', reservation_number);
    if (client_first_name) queryParams.append('client_first_name', client_first_name);
    if (client_last_name) queryParams.append('client_last_name', client_last_name);

    const response = await apiClient.get(`api/pagination/trips/observations?${queryParams.toString()}`);
    return response.data;
};

export const getTripObservationById = async (observationId) => {
    const response = await apiClient.get(`api/trips/observations/${observationId}`);
    return response.data;
};

export const getActivityObservations = async (params = {}) => {
    const {
        skip = 0,
        limit = 10,
        policy_code,
        activity_start_date,
        reservation_number,
        client_first_name,
        client_last_name,
        company_name,
    } = params;

    const queryParams = new URLSearchParams();
    queryParams.append('skip', skip.toString());
    queryParams.append('limit', limit.toString());

    if (policy_code) queryParams.append('policy_code', policy_code);
    if (activity_start_date) queryParams.append('activity_start_date', activity_start_date);
    if (reservation_number) queryParams.append('reservation_number', reservation_number);
    if (client_first_name) queryParams.append('client_first_name', client_first_name);
    if (client_last_name) queryParams.append('client_last_name', client_last_name);
    if (company_name) queryParams.append('company_name', company_name);

    const response = await apiClient.get(`api/pagination/observations?${queryParams.toString()}`);
    return response.data;
};

export const getActivityObservationById = async (observationId) => {
    const response = await apiClient.get(`api/observations/${observationId}`);
    return response.data;
};

export const deleteActivityObservation = async (observationId) => {
    const response = await apiClient.delete(`api/observations/${observationId}`);
    return response.data;
};

export const getTripById = async (tripId) => {
    const response = await apiClient.get(`api/partners/trips/${tripId}`);
    return response.data;
};

export const createTrip = async (partnerId, tripData) => {
    const response = await apiClient.post(`api/trips/partners/${partnerId}`, tripData);
    return response.data;
};

export const updateTrip = async (tripId, tripData) => {
    const response = await apiClient.put(`api/partners/trips/${tripId}`, tripData);
    return response.data;
};

export const deleteTrip = async (tripId) => {
    const response = await apiClient.delete(`api/trips/${tripId}`);
    return response.data;
};

export const createSubcoveragesTrip = async (partnerId, subcoveragesData) => {
    const response = await apiClient.post(`api/subcoverages-trip/partner/${partnerId}`, subcoveragesData);
    return response.data;
};

export const getSubcoveragesByPartner = async (partnerId) => {
    const response = await apiClient.get(`api/subcoverages-trip/partner/${partnerId}`);
    return response.data;
};

// Import Excel file for Olela Policies
export const importTripsOlela = async (partnerId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(`api/import-trips/olela/policies/${partnerId}`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
        timeout: 0,
    });
    return response.data;
};

// Import Excel file for Flower Policies
export const importTripsFlower = async (partnerId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(`api/import-trips/flower/policies/${partnerId}`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
        timeout: 0,
    });
    return response.data;
};

// Import Excel file for Lacdesgraves Policies
export const importTripsLacdesgraves = async (partnerId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(`api/import-trips/lacdesgraves/policies/${partnerId}`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
        timeout: 0,
    });
    return response.data;
};

// Import Excel file for Aloa Vacances Policies
export const importTripsAloa = async (partnerId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(`api/import-trips/aloa/policies/${partnerId}`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
        timeout: 0,
    });
    return response.data;
};

// Import Excel file for Odalys Vacances Policies
export const importTripsOdalys = async (partnerId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(`api/import-trips/odalys/policies/${partnerId}`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
        timeout: 0,
    });
    return response.data;
};

/** Import standard multi-partenaires (company_name par ligne) */
export const importTripsPoliciesStandard = async (file, params = {}) => {
    const {
        dry_run = false,
        sheet_name,
        strict_company_check = true,
    } = params;
    const queryParams = new URLSearchParams();
    queryParams.append('dry_run', String(dry_run));
    queryParams.append('strict_company_check', String(strict_company_check));
    if (sheet_name != null && String(sheet_name).trim() !== '') {
        queryParams.append('sheet_name', String(sheet_name).trim());
    }
    const formData = new FormData();
    formData.append('file', file);
    const qs = queryParams.toString();
    const url = qs
        ? `api/standard/trips/import-new-policies?${qs}`
        : 'api/standard/trips/import-new-policies';
    try {
        const response = await apiClient.post(url, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
            timeout: 0,
        });
        return response.data;
    } catch (error) {
        if (error?.response?.data) {
            return error.response.data;
        }
        throw error;
    }
};

// Import Excel file for Green River Cruises Activity Policies
export const importActivityPoliciesGreenRiverCruises = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post('api/green-river-cruises/import-policies', formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
        timeout: 300000, // 5 minutes (300000ms) pour permettre le traitement de gros fichiers
    });
    return response.data;
};

// Import Excel file for Flower Cancelled Policies
const patchStructuredTripCancellationImport = async (path, file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
        const response = await apiClient.patch(path, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
            timeout: 0,
        });
        return response.data;
    } catch (error) {
        const data = error?.response?.data;
        if (
            data
            && (
                typeof data.code === 'string'
                || Array.isArray(data.processed_results)
                || data.success === true
            )
        ) {
            return data;
        }
        throw error;
    }
};

export const importFlowerPoliciesCancelled = async (file) => (
    patchStructuredTripCancellationImport('api/import/flower/trips/policies/trip-cancelled', file)
);

/** Mise à jour des numéros de téléphone clients depuis Excel */
export const importUpdateClientPhoneNumbers = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
        const response = await apiClient.patch('api/import/clients/phone-number', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
            timeout: 0,
        });
        return response.data;
    } catch (error) {
        if (error?.response?.data) {
            return error.response.data;
        }
        throw error;
    }
};

// Import Excel file for Trip Cancelled Policies (standard format)
export const importTripsPoliciesCancelledGeneric = async (file) => (
    patchStructuredTripCancellationImport('api/import/standard/trips/policies/trip-cancelled', file)
);

// Import Excel file for Trip Modified (standard format)
export const importTripsTripModifiedGeneric = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.patch('api/import/standard/trip-modified', formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
        timeout: 300000,
    });
    return response.data;
};

// Import Excel file for Flower Trip Modified
export const importFlowerTripModified = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.patch(`api/import/flower/trip-modified`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
        timeout: 300000, // 5 minutes (300000ms) pour permettre le traitement de gros fichiers
    });
    return response.data;
};

// Import Excel file for Lacdesgraves Trip Modified
export const importLacdesgravesTripModified = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.patch(`api/import/lac-des-graves/trip-modified`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
        timeout: 300000, // 5 minutes (300000ms) pour permettre le traitement de gros fichiers
    });
    return response.data;
};

// Import Excel file for Olela Trip Modified
export const importOlelaTripModified = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.patch(`api/import/olela/trip-modified`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
        timeout: 300000, // 5 minutes (300000ms) pour permettre le traitement de gros fichiers
    });
    return response.data;
};

// Import Excel file for Aloa Vacances Trip Modified
export const importAloaTripModified = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.patch(`api/import/aloa/trip-modified`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
        timeout: 300000, // 5 minutes (300000ms) pour permettre le traitement de gros fichiers
    });
    return response.data;
};

// Import Excel/CSV file for Odalys Vacances Trip Modified
export const importOdalysTripModified = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.patch('api/import/odalys/trip-modified', formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
        timeout: 300000,
    });
    return response.data;
};

// Import Excel file for Lacdesgraves Cancelled Policies
export const importLacdesgravesPoliciesCancelled = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.patch(`api/import/lacdesgraves/trips/policies/trip-cancelled`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
        timeout: 300000, // 5 minutes (300000ms) pour permettre le traitement de gros fichiers
    });
    return response.data;
};

// Import Excel file for Olela Cancelled Policies
export const importOlelaPoliciesCancelled = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.patch(`api/import/olela/trips/policies/trip-cancelled`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
        timeout: 300000, // 5 minutes (300000ms) pour permettre le traitement de gros fichiers
    });
    return response.data;
};

// Import Excel file for Odalys Vacances Cancelled Policies
export const importOdalysPoliciesCancelled = async (file) => (
    patchStructuredTripCancellationImport('api/import/odalys/trips/policies/trip-cancelled', file)
);

// Import Excel file for Aloa Vacances Cancelled Policies
export const importAloaPoliciesCancelled = async (file) => (
    patchStructuredTripCancellationImport('api/import/aloa/trips/policies/trip-cancelled', file)
);

// Import Excel file for References and Subcoverages
export const importReferencesSubcoverages = async (partnerId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(`api/trips/import-references-subcov/partners/${partnerId}`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
        timeout: 300000, // 5 minutes (300000ms) pour permettre le traitement de gros fichiers
    });
    return response.data;
};

// Update references and subcoverages from Excel export format
export const updateReferencesSubcoverages = async (partnerId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.put(`api/trips/import-references-subcov/partners/${partnerId}/update`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
        timeout: 300000, // 5 minutes (300000ms) pour permettre le traitement de gros fichiers
    });
    return response.data;
};

// Import claim updates from Excel for Flower
export const importClaimsBulkUpdatesFlower = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.put('api/trips/flower/claims/bulk-updates/payments', formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
        timeout: 300000, // 5 minutes (300000ms) pour permettre le traitement de gros fichiers
    });
    return response.data;
};

// Import claim updates from Excel for Olela
export const importClaimsBulkUpdatesOlela = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.put('api/trips/olela/claims/bulk-updates/payments', formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
        timeout: 300000, // 5 minutes (300000ms) pour permettre le traitement de gros fichiers
    });
    return response.data;
};

// Import claim updates from Excel for B2C
export const importClaimsBulkUpdatesB2C = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.put('api/trips/b2c/claims/bulk-updates/payments', formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
        timeout: 300000, // 5 minutes (300000ms) pour permettre le traitement de gros fichiers
    });
    return response.data;
};

// Import claim payment updates from Excel — tous partenaires (import paid daily)
export const importClaimsBulkUpdatesGeneric = async (file, params = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    const queryParams = new URLSearchParams();
    const companyNames = Array.isArray(params.company_name)
        ? params.company_name
        : params.company_name != null && String(params.company_name).trim() !== ''
            ? [String(params.company_name).trim()]
            : [];
    companyNames
        .map((n) => String(n).trim())
        .filter((n) => n !== '')
        .forEach((n) => queryParams.append('company_name', n));
    const qs = queryParams.toString();
    const url = qs
        ? `api/trips/claims/bulk-updates/payments?${qs}`
        : 'api/trips/claims/bulk-updates/payments';
    const response = await apiClient.put(url, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
        timeout: 300000, // 5 minutes (300000ms) pour permettre le traitement de gros fichiers
    });
    return response.data;
};

// Import claim payment updates from Excel — partenaires Standard uniquement
export const importClaimsBulkUpdatesStandard = async (file, params = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    const queryParams = new URLSearchParams();
    const companyNames = Array.isArray(params.company_name)
        ? params.company_name
        : params.company_name != null && String(params.company_name).trim() !== ''
            ? [String(params.company_name).trim()]
            : [];
    companyNames
        .map((n) => String(n).trim())
        .filter((n) => n !== '')
        .forEach((n) => queryParams.append('company_name', n));
    const qs = queryParams.toString();
    const url = qs
        ? `api/standard/trips/claims/bulk-updates/payments?${qs}`
        : 'api/standard/trips/claims/bulk-updates/payments';
    const response = await apiClient.put(url, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
        timeout: 300000,
    });
    return response.data;
};

// API Observations
export const getObservationsActivities = async () => {
    const response = await apiClient.get('api/observations');
    return response.data;
};

// API Activity References
export const getActivityReferences = async (params = {}) => {
    const {
        skip = 0,
        limit = 10,
        name,
        activity_type,
        category,
        company_name,
    } = params;

    const queryParams = new URLSearchParams();
    queryParams.append('skip', skip.toString());
    queryParams.append('limit', limit.toString());

    if (name) queryParams.append('name', name);
    if (activity_type) queryParams.append('activity_type', activity_type);
    if (category) queryParams.append('category', category);
    if (company_name) queryParams.append('company_name', company_name);

    const response = await apiClient.get(`api/pagination/activityreferences/?${queryParams.toString()}`);
    return response.data;
};

export const getActivityReferenceById = async (referenceId) => {
    const response = await apiClient.get(`api/activity-references/${referenceId}`);
    return response.data;
};

export const getActivityReferencesByPartner = async (partnerId) => {
    const response = await apiClient.get(`api/activity-references/partners/${partnerId}`);
    return response.data;
};

export const createActivityReference = async (partnerId, referenceData) => {
    const response = await apiClient.post(`api/partners/activityreferences/${partnerId}`, referenceData);
    return response.data;
};

export const updateActivityReference = async (referenceId, referenceData) => {
    const response = await apiClient.put(`api/partners/activityreferences/${referenceId}`, referenceData);
    return response.data;
};

export const deleteActivityList = async (referenceId) => {
    const response = await apiClient.delete(`api/partners/activitylist/${referenceId}`);
    return response.data;
};

// API Activities — GET /api/pagination/activities
export const getActivities = async (params = {}) => {
    const {
        skip = 0,
        limit = 10,
        category,
        name,
        activity_start_date_from,
        activity_start_date_to,
        activity_end_date_from,
        activity_end_date_to,
        company_name,
        activity_code,
        reservation_number,
        client_first_name,
        client_last_name,
        client_email,
        first_name,
        last_name,
        email,
    } = params;

    const queryParams = new URLSearchParams();
    const safeSkip = Math.max(Number(skip) || 0, 0);
    const safeLimit = Math.min(Math.max(Number(limit) || 10, 1), 100);
    queryParams.append('skip', safeSkip.toString());
    queryParams.append('limit', safeLimit.toString());

    const appendOptional = (key, value) => {
        if (value == null) return;
        const normalized = typeof value === 'string' ? value.trim() : String(value).trim();
        if (normalized !== '') {
            queryParams.append(key, normalized);
        }
    };

    appendOptional('category', category);
    appendOptional('name', name);
    appendOptional('activity_start_date_from', activity_start_date_from);
    appendOptional('activity_start_date_to', activity_start_date_to);
    appendOptional('activity_end_date_from', activity_end_date_from);
    appendOptional('activity_end_date_to', activity_end_date_to);
    appendOptional('company_name', company_name);
    appendOptional('activity_code', activity_code);
    appendOptional('reservation_number', reservation_number);
    appendOptional('client_first_name', client_first_name || first_name);
    appendOptional('client_last_name', client_last_name || last_name);
    appendOptional('client_email', client_email || email);

    const response = await apiClient.get(`api/pagination/activities?${queryParams.toString()}`);
    return response.data;
};

export const getActivityById = async (activityId) => {
    const response = await apiClient.get(`api/partners/activities/${activityId}`);
    return response.data;
};

export const createActivity = async (partnerId, activityData) => {
    const response = await apiClient.post(`api/partners/${partnerId}/activities`, activityData);
    return response.data;
};

export const updateActivity = async (activityId, activityData) => {
    const response = await apiClient.put(`api/partners/activities/${activityId}`, activityData);
    return response.data;
};

export const deleteActivity = async (activityId) => {
    const response = await apiClient.delete(`api/activities/${activityId}`);
    return response.data;
};

// Activities Quotes
export const getActivitiesQuotes = async (params = {}) => {
    const response = await apiClient.get('api/quotes', { params });
    return response.data;
};

export const getActivityQuoteById = async (quoteId) => {
    const response = await apiClient.get(`api/quotes/${quoteId}`);
    return response.data;
};

export const createActivityQuote = async (activityId) => {
    const response = await apiClient.post(`api/quotes/${activityId}`);
    return response.data;
};

export const createPolicy = async (quoteId, clientId, policyData) => {
    const response = await apiClient.post(`api/policies/${quoteId}/clients/${clientId}`, policyData, {
        timeout: 120000, // 2 minutes (120000ms) pour permettre le traitement de la création de policy
    });
    return response.data;
};

// API Clients
export const getClients = async (params = {}) => {
    const {
        skip = 0,
        limit = 10,
        email,
        first_name,
        last_name,
        phone_number,
        lcbft_checked,
    } = params;

    const queryParams = new URLSearchParams();
    queryParams.append('skip', skip.toString());
    queryParams.append('limit', limit.toString());

    if (email) queryParams.append('email', email);
    if (first_name) queryParams.append('first_name', first_name);
    if (last_name) queryParams.append('last_name', last_name);
    if (phone_number) queryParams.append('phone_number', phone_number);
    if (lcbft_checked !== undefined && lcbft_checked !== null) {
        queryParams.append('lcbft_checked', lcbft_checked.toString());
    }

    const response = await apiClient.get(`api/pagination/clients?${queryParams.toString()}`);
    return response.data;
};

export const getClientsByPartner = async (partnerId, params = {}) => {
    const { email } = params;
    let url = `api/clients/partner/${partnerId}`;

    const queryParams = new URLSearchParams();
    if (email) {
        queryParams.append('email', email);
    }

    if (queryParams.toString()) {
        url += `?${queryParams.toString()}`;
    }

    const response = await apiClient.get(url);
    return response.data;
};

// API Bulk Transfers
export const generateBulkTransfersCsv = async (params = {}) => {
    const { claim_date } = params;
    let url = 'api/trips/claims/bulk-transfers/csv';

    const queryParams = new URLSearchParams();
    if (claim_date) {
        queryParams.append('claim_date', claim_date);
    }

    if (queryParams.toString()) {
        url += `?${queryParams.toString()}`;
    }

    const response = await apiClient.post(url);
    return response.data;
};

export const generateBulkTransfersCsvByPolicyCodes = async (params = {}) => {
    const { policy_codes, claim_date } = params;
    let url = 'api/trips/claims/bulk-transfers/csv/by-policy-codes';

    const queryParams = new URLSearchParams();
    if (policy_codes && Array.isArray(policy_codes) && policy_codes.length > 0) {
        policy_codes.forEach(code => {
            queryParams.append('policy_codes', code);
        });
    }
    if (claim_date) {
        queryParams.append('claim_date', claim_date);
    }

    if (queryParams.toString()) {
        url += `?${queryParams.toString()}`;
    }

    const response = await apiClient.post(url);
    return response.data;
};

export const generateBulkTransfersCsvEndOfStay = async (params = {}) => {
    const { trip_end_date_from, trip_end_date_to } = params;
    let url = 'api/trips/claims/bulk-transfers/csv/end-of-stay';

    const queryParams = new URLSearchParams();
    if (trip_end_date_from) {
        queryParams.append('trip_end_date_from', trip_end_date_from);
    }
    if (trip_end_date_to) {
        queryParams.append('trip_end_date_to', trip_end_date_to);
    }

    if (queryParams.toString()) {
        url += `?${queryParams.toString()}`;
    }

    const response = await apiClient.post(url);
    return response.data;
};

export const generateBulkTransfersCsvEndOfStayByPolicyCodes = async (params = {}) => {
    const { policy_codes } = params;
    let url = 'api/trips/claims/bulk-transfers/csv/end-of-stay/by-policy-codes';

    const queryParams = new URLSearchParams();
    if (policy_codes && Array.isArray(policy_codes) && policy_codes.length > 0) {
        policy_codes.forEach(code => {
            queryParams.append('policy_codes', code);
        });
    }

    if (queryParams.toString()) {
        url += `?${queryParams.toString()}`;
    }

    const response = await apiClient.post(url);
    return response.data;
};

// ---------- Commercial Gesture (API Standard) ----------
const COMMERCIAL_GESTURE_LIST_KEYS = ['page', 'per_page', 'status', 'policy_code', 'reservation_number', 'search'];

export const listCommercialGestures = async (params = {}) => {
    const queryParams = new URLSearchParams();
    COMMERCIAL_GESTURE_LIST_KEYS.forEach((key) => {
        const value = params[key];
        if (value === null || value === undefined || String(value).trim() === '') return;
        queryParams.append(key, String(value).trim());
    });
    const qs = queryParams.toString();
    const response = await apiClient.get(
        `api/standard/commercial-gestures${qs ? `?${qs}` : ''}`,
        { skipCache: true }
    );
    return response.data;
};

export const getCommercialGesture = async (gestureId) => {
    const response = await apiClient.get(
        `api/standard/commercial-gestures/${encodeURIComponent(gestureId)}`,
        { skipCache: true }
    );
    return response.data;
};

export const createCommercialGesture = async ({ policy_id, amount }) => {
    const response = await apiClient.post('api/standard/commercial-gestures', { policy_id, amount });
    return response.data;
};

export const updateCommercialGesture = async (gestureId, changes) => {
    const response = await apiClient.put(
        `api/standard/commercial-gestures/${encodeURIComponent(gestureId)}`,
        changes
    );
    return response.data;
};

export const deleteCommercialGesture = async (gestureId) => {
    await apiClient.delete(`api/standard/commercial-gestures/${encodeURIComponent(gestureId)}`);
};

export const sendCommercialGestureRibLink = async (gestureId) => {
    const response = await apiClient.post(
        `api/standard/commercial-gestures/${encodeURIComponent(gestureId)}/send-rib-link`
    );
    return response.data;
};

export const getPolicyCommercialGesture = async (policyId) => {
    const response = await apiClient.get(
        `api/standard/policies/${encodeURIComponent(policyId)}/commercial-gesture`,
        { skipCache: true }
    );
    return response.data;
};

export const createPolicyCommercialGesture = async (policyId, { amount }) => {
    const response = await apiClient.post(
        `api/standard/policies/${encodeURIComponent(policyId)}/commercial-gesture`,
        { amount }
    );
    return response.data;
};

export const generateStandardEndOfStayTransferFiles = async (params = {}) => {
    const { trip_end_date_from, trip_end_date_to, policy_code } = params;
    const queryParams = new URLSearchParams();
    if (trip_end_date_from) queryParams.append('trip_end_date_from', trip_end_date_from);
    if (trip_end_date_to) queryParams.append('trip_end_date_to', trip_end_date_to);
    if (policy_code && policy_code.trim()) queryParams.append('policy_code', policy_code.trim());

    const query = queryParams.toString();
    const url = `api/standard/trips/claims/bulk-transfers/csv/end-of-stay${query ? `?${query}` : ''}`;
    const response = await apiClient.post(url);
    return response.data;
};

export const generateStandardEndOfStayTransferFilesByPolicyCodes = async (policyCodes = []) => {
    const queryParams = new URLSearchParams();
    policyCodes.forEach((code) => queryParams.append('policy_codes', code));
    const url = `api/standard/trips/claims/bulk-transfers/csv/end-of-stay/by-policy-codes?${queryParams.toString()}`;
    const response = await apiClient.post(url);
    return response.data;
};

export const generateStandardCommercialGestureTransferFiles = async (policyCodes = []) => {
    const queryParams = new URLSearchParams();
    policyCodes.forEach((code) => queryParams.append('policy_codes', code));
    const query = queryParams.toString();
    const url = `api/standard/commercial-gestures/bulk-transfers/csv${query ? `?${query}` : ''}`;
    const response = await apiClient.post(url);
    return response.data;
};

export const importStandardCommercialGesturePayments = async (file, params = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    const queryParams = new URLSearchParams();
    const companyName = params.company_name != null ? String(params.company_name).trim() : '';
    if (companyName) queryParams.append('company_name', companyName);
    const query = queryParams.toString();
    const url = `api/standard/commercial-gestures/bulk-updates/payments${query ? `?${query}` : ''}`;
    const response = await apiClient.put(url, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000,
    });
    return response.data;
};

// API pour télécharger un fichier depuis S3
export const downloadS3File = async (s3Path) => {
    const params = { s3_path: s3Path };

    // Ajouter les clés AWS si disponibles
    const accessKeyId = getAwsAccessKeyId();
    const secretAccessKey = getAwsSecretAccessKey();

    if (accessKeyId && secretAccessKey) {
        params.aws_access_key_id = accessKeyId;
        params.aws_secret_access_key = secretAccessKey;
    }

    const response = await apiClient.get(`api/files/download`, {
        params: params,
        responseType: 'blob', // Important pour télécharger un fichier binaire
    });
    return response;
};

/**
 * Télécharge un template Excel du bucket ``excels-templates``.
 * GET /files/download?file=template_import_policies_standard.xlsx
 * @param {string} fileName Nom du fichier (ex. template_import_policies_standard.xlsx)
 */
export const downloadExcelTemplate = async (fileName) => {
    const response = await apiClient.get('api/files/download', {
        params: { file: String(fileName).trim() },
        responseType: 'blob',
    });
    return response;
};

export const getClientById = async (clientId) => {
    const response = await apiClient.get(`api/clients/${clientId}`);
    return response.data;
};

export const getClientLcbft = async (clientId) => {
    const response = await apiClient.get(`api/clients/${clientId}/lcbft`);
    return response.data;
};

export const createClient = async (clientData) => {
    const response = await apiClient.post('api/clients', clientData);
    return response.data;
};

export const updateClient = async (clientId, clientData) => {
    const response = await apiClient.put(`api/clients/${clientId}`, clientData);
    return response.data;
};

export const deleteClient = async (clientId) => {
    const response = await apiClient.delete(`api/clients/${clientId}`);
    return response.data;
};

export const checkLcbftConformity = async (clientId) => {
    const response = await apiClient.post(`api/lcb-ft/check-conformity?client_id=${clientId}`);
    return response.data;
};

// API Partners
export const getPartners = async (params = {}) => {
    const {
        skip = 0,
        limit = 10,
        email,
        company_name,
        first_name,
        last_name,
    } = params;

    const queryParams = new URLSearchParams();
    queryParams.append('skip', skip.toString());
    queryParams.append('limit', limit.toString());

    if (email) queryParams.append('email', email);
    if (company_name) queryParams.append('company_name', company_name);
    if (first_name) queryParams.append('first_name', first_name);
    if (last_name) queryParams.append('last_name', last_name);

    const response = await apiClient.get(`pagination/partners?${queryParams.toString()}`);
    return response.data;
};

// Get all partners without pagination - GET /partners
export const getAllPartners = async () => {
    const response = await apiClient.get('partners');
    return response.data;
};

// Get partner by ID - GET /partners/{partnerId}
export const getPartnerById = async (partnerId) => {
    const response = await apiClient.get(`partners/${partnerId}`);
    return response.data;
};

/** Création compte utilisateur partenaire (corps aligné sur UserAccount côté API) */
export const createPartner = async (partnerData) => {
    const response = await apiClient.post('partner-accounts/create', partnerData);
    return response.data;
};

// Update partner - PUT /partners/{partnerId} (same endpoint URL as getPartnerById, different method)
export const updatePartner = async (partnerId, partnerData) => {
    const response = await apiClient.put(`partners/${partnerId}`, partnerData);
    return response.data;
};

export const deletePartner = async (partnerId) => {
    const response = await apiClient.delete(`users/${partnerId}`);
    return response.data;
};

export const createPartnerAccount = async (accountData) => {
    const response = await apiClient.post('partner-accounts/create', accountData);
    return response.data;
};

// Email Management
export const getEmailManagement = async (params = {}) => {
    const {
        skip = 0,
        limit = 10,
        email,
        company_name,
    } = params;

    const queryParams = new URLSearchParams();
    queryParams.append('skip', skip.toString());
    queryParams.append('limit', limit.toString());

    if (email) queryParams.append('email', email);
    if (company_name) queryParams.append('company_name', company_name);

    const response = await apiClient.get(`api/pagination/email_management?${queryParams.toString()}`);
    return response.data;
};

export const getEmailManagementById = async (emailManagementId) => {
    const response = await apiClient.get(`api/email_management/${emailManagementId}`);
    return response.data;
};

export const createEmailManagement = async (partnerId, emailManagementData) => {
    const response = await apiClient.post(`api/partners/email_management/${partnerId}`, emailManagementData);
    return response.data;
};

export const updateEmailManagement = async (emailManagementId, emailManagementData) => {
    const response = await apiClient.put(`api/partners/email_management/${emailManagementId}`, emailManagementData);
    return response.data;
};

export const deleteEmailManagement = async (emailManagementId) => {
    const response = await apiClient.delete(`api/partners/email_management/${emailManagementId}`);
    return response.data;
};

// SMS Management API
export const getSmsManagement = async (params = {}) => {
    const {
        skip = 0,
        limit = 10,
        email,
        company_name,
        sms_processes,
    } = params;

    const queryParams = new URLSearchParams();
    queryParams.append('skip', skip.toString());
    queryParams.append('limit', limit.toString());

    if (email) queryParams.append('email', email);
    if (company_name) queryParams.append('company_name', company_name);
    if (Array.isArray(sms_processes)) {
        sms_processes.forEach((process) => queryParams.append('sms_processes', process));
    }

    const response = await apiClient.get(`api/pagination/sms_management?${queryParams.toString()}`);
    return response.data;
};

export const getSmsManagementById = async (smsManagementId) => {
    const response = await apiClient.get(`api/sms_management/${smsManagementId}`);
    return response.data;
};

export const getSmsManagementByPartnerId = async (partnerId) => {
    const response = await apiClient.get(`api/sms_management/partner/${partnerId}`);
    return response.data;
};

export const getAllSmsManagement = async () => {
    const response = await apiClient.get('api/sms_management');
    return response.data;
};

export const createSmsManagement = async (partnerId, smsManagementData) => {
    const response = await apiClient.post(`api/partners/sms_management/${partnerId}`, smsManagementData);
    return response.data;
};

export const updateSmsManagement = async (smsManagementId, smsManagementData) => {
    const response = await apiClient.put(`api/partners/sms_management/${smsManagementId}`, smsManagementData);
    return response.data;
};

export const deleteSmsManagement = async (smsManagementId) => {
    const response = await apiClient.delete(`api/partners/sms_management/${smsManagementId}`);
    return response.data;
};

// SMS Monitor (Twilio read-only)
export const getAdminSmsList = async (filters = {}) => {
    const {
        page = 1,
        page_size = 20,
        status,
        date_from,
        date_to,
        to_number,
        from_number,
    } = filters;

    const queryParams = new URLSearchParams();
    queryParams.append('page', String(page));
    queryParams.append('page_size', String(page_size));
    if (status) queryParams.append('status', status);
    if (date_from) queryParams.append('date_from', date_from);
    if (date_to) queryParams.append('date_to', date_to);
    if (to_number) queryParams.append('to_number', to_number.trim());
    if (from_number) queryParams.append('from_number', from_number.trim());

    const response = await apiClient.get(`api/admin/sms?${queryParams.toString()}`);
    return response.data;
};

export const getAdminSmsStatistics = async ({ date_from, date_to } = {}) => {
    const queryParams = new URLSearchParams();
    if (date_from) queryParams.append('date_from', date_from);
    if (date_to) queryParams.append('date_to', date_to);
    const qs = queryParams.toString();
    const url = qs ? `api/admin/sms/statistics?${qs}` : 'api/admin/sms/statistics';
    const response = await apiClient.get(url);
    return response.data;
};

export const getAdminSmsById = async (messageSid) => {
    const response = await apiClient.get(`api/admin/sms/${messageSid}`);
    return response.data;
};

// Email monitor (Brevo, read-only)
const appendNormalizedBrevoEventParams = (queryParams, { event, event_type: eventTypeAlias } = {}, paramName = 'event') => {
    const raw = event ?? eventTypeAlias;
    if (!raw) return;
    const normalized = resolveBrevoEventFilter(raw);
    queryParams.append(paramName, normalized);
};

export const getAdminEmailList = async (filters = {}) => {
    const {
        page = 1,
        page_size = 20,
        email,
        client_email,
        template_id,
        message_id,
        subject,
        event,
        event_type,
        date_from,
        date_to,
    } = filters;

    const queryParams = new URLSearchParams();
    queryParams.append('page', String(page));
    queryParams.append('page_size', String(page_size));
    const trimmedClientEmail = String(client_email || '').trim();
    const trimmedEmail = String(email || '').trim();
    if (trimmedClientEmail) {
        queryParams.append('client_email', trimmedClientEmail);
    } else if (trimmedEmail) {
        queryParams.append('email', trimmedEmail);
    }
    if (template_id != null && String(template_id).trim() !== '') {
        queryParams.append('template_id', String(template_id).trim());
    }
    if (message_id) queryParams.append('message_id', String(message_id).trim());
    const trimmedSubject = String(subject || '').trim();
    if (trimmedSubject) {
        queryParams.append('subject', trimmedSubject);
    } else {
        appendNormalizedBrevoEventParams(queryParams, { event, event_type }, 'event_type');
    }
    if (date_from) queryParams.append('date_from', date_from);
    if (date_to) queryParams.append('date_to', date_to);

    const response = await apiClient.get(`api/admin/email?${queryParams.toString()}`);
    return response.data;
};

export const getAdminEmailEvents = async (filters = {}) => {
    const {
        page = 1,
        page_size = 50,
        email,
        client_email,
        event,
        event_type,
        tags,
        message_id,
        template_id,
        date_from,
        date_to,
    } = filters;

    const queryParams = new URLSearchParams();
    queryParams.append('page', String(page));
    queryParams.append('page_size', String(page_size));
    const trimmedClientEmail = String(client_email || '').trim();
    const trimmedEmail = String(email || '').trim();
    if (trimmedClientEmail) {
        queryParams.append('client_email', trimmedClientEmail);
    } else if (trimmedEmail) {
        queryParams.append('email', trimmedEmail);
    }
    appendNormalizedBrevoEventParams(queryParams, { event, event_type }, 'event');
    if (tags) queryParams.append('tags', String(tags).trim());
    if (message_id) queryParams.append('message_id', String(message_id).trim());
    if (template_id != null && String(template_id).trim() !== '') {
        queryParams.append('template_id', String(template_id).trim());
    }
    if (date_from) queryParams.append('date_from', date_from);
    if (date_to) queryParams.append('date_to', date_to);

    const response = await apiClient.get(`api/admin/email/events?${queryParams.toString()}`);
    return response.data;
};

export const getAdminEmailStatistics = async ({ date_from, date_to } = {}) => {
    const queryParams = new URLSearchParams();
    if (date_from) queryParams.append('date_from', date_from);
    if (date_to) queryParams.append('date_to', date_to);
    const qs = queryParams.toString();
    const url = qs ? `api/admin/email/statistics?${qs}` : 'api/admin/email/statistics';
    const response = await apiClient.get(url);
    return response.data;
};

export const getAdminEmailByUuid = async (messageUuid) => {
    const response = await apiClient.get(`api/admin/email/${encodeURIComponent(messageUuid)}`);
    return response.data;
};

// Mailing List API
export const getMailingList = async (config = {}) => {
    // Utiliser forceRefresh pour contourner le cache si nécessaire
    const requestConfig = config.forceRefresh ? { forceRefresh: true } : {};
    const response = await apiClient.get('api/admin/mailing-list', requestConfig);
    return response.data;
};

export const createMailingList = async (mailingListData) => {
    const response = await apiClient.post('api/admin/mailing-list/create', mailingListData);
    // La réponse peut être au format { message, data } pour le statut 201
    return response.data;
};

export const updateMailingListEntry = async (emailId, mailingListData) => {
    const response = await apiClient.put(`api/admin/mailing-list/${emailId}`, mailingListData);
    return response.data;
};

export const deleteMailingListEntry = async (emailId) => {
    const response = await apiClient.delete(`api/admin/mailing-list/${emailId}`);
    return response.data;
};

// Orakle weather API Authentication (OAuth2 Client Credentials)
export const authenticate_orakle_weather = async (clientId, clientSecret, audience) => {
    const response = await apiClient.post('oauth/token', {
        grant_type: 'client_credentials',
        client_id: clientId,
        client_secret: clientSecret,
        audience: audience,
    }, {
        headers: {
            'Content-Type': 'application/json',
        },
    });
    return response.data;
};

// Client Policies API
export const getClientPolicies = async (clientEmail) => {
    const response = await apiClient.get(`api/client/policies/${clientEmail}`);
    return response.data;
};

export default apiClient;

